// apps/dylan — single-file React front-end. Auth gate (sign in / create account) over the
// core's /api endpoints, then an authenticated app shell with the D1 dashboard tiles (static
// at DY·1; they go live link by link). esbuild bundles this to public/app.js.
//
// Dylan reviews in EXTERNAL ANKI — there is no in-app review surface here, ever.
import { useState, useEffect, useRef } from "react"; // useRef: the Y·4 syllabus intake's row keys
import { createRoot } from "react-dom/client";
// DY·2: manual per-course letter grades on the core standard 4.0 map; unweighted average
// (fixed-cohort dental curriculum — credit-weighting is Alivia's difference).
import { GRADES, gpaOf, fmtGpa } from "../gpa.js";
import { localDateStr, addDays, byDue, latestTerm } from "@school-apps/core/lib/dates";
import { renderMarkdownSubset } from "@school-apps/core/lib/markdown"; // C·5: safe markdown on the pending-export list
import { CARD_IMAGE_MAX_BYTES } from "@school-apps/core/images/limits"; // SF·1 — the crop must fit the same cap the attach route enforces
import { isAllowedImageUrl, NEAR_BLACK_LUMINANCE, LUMINANCE_SAMPLE_PX } from "@school-apps/core/images/limits"; // IMG·S·4: the post-fetch redirect check; IMG·Q·3: the near-black threshold (client-safe module)
import { hoursCsv } from "@school-apps/core/hours/csv"; // A·3: client-side CSV export of the hour log
import { computePacing } from "@school-apps/core/lib/pacing"; // A·3: the anti-all-nighter arithmetic (pure, no AI)
import { activeCourses, archivedCourses, forActiveCourses } from "@school-apps/core/hub/archive"; // ARC·1: the one "archived" rule (pure)
import { RAMP_WINDOW_DAYS } from "@school-apps/core/exams/exams"; // E·4: the countdown's soon-highlight ONLY — Dylan gets no pre-exam ramp (no srs; the ramp seam is grep-proven absent from his source)
import { expandOccurrences } from "@school-apps/core/calendar/occurrences"; // CAL·6b: flatten meetings/events/homework/exams into month-grid occurrences (pure, tz-free — no srs)
import { validateEvent, EVENT_KINDS } from "@school-apps/core/hub/events-validate"; // CAL·6b: the SAME pure validator the server enforces, gating the Add/Edit form's Save
// DY·4: the AnkiConnect adapter — CLIENT-SIDE (his browser talks to localhost:8765; the
// droplet never calls Anki). One client for the whole app; failures come back shaped.
import { createAnkiClient, ANKI_DECK } from "./anki.js";

const anki = createAnkiClient();

// --- tiny API client (same-origin; the session cookie rides along via credentials:include) ---
const JSON_HEADERS = { "content-type": "application/json" };

async function req(method, path, body) {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body === undefined ? undefined : JSON_HEADERS,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty / non-JSON body */ }
  return { ok: res.ok, status: res.status, data };
}

const api = {
  async me() {
    try {
      const r = await req("GET", "/api/me");
      return r.ok && r.data ? r.data.user : null;
    } catch {
      return null; // no network / not signed in -> treat as signed out
    }
  },
  signIn: (email, password) => req("POST", "/api/login", { email, password }),
  createAccount: (email, password) => req("POST", "/api/register", { email, password }),
  async signOut() { try { await req("POST", "/api/logout"); } catch { /* ignore */ } },
  // S2·2 — change password (requires the current one; server audits, never logs material)
  changePassword: (currentPassword, newPassword) => req("POST", "/api/change-password", { currentPassword, newPassword }),

  // DY·3: card capture — AI preview (server-authoritative, never persisted), then a confirmed
  // bulk save into the pending-export queue. Delivery is Anki; there is no review here.
  cards: {
    // IMG·4 — images on cards. `imageSuggest` is PREVIEW-ONLY (the server persists nothing); only
    // `imageAttach`, driven by his tap, stores anything. An attached image rides his Anki push as a
    // media file (src/anki.js) — it is a field on the card, never schedule state.
    // IMG·Q·1 — `query` (optional) is HIS OWN words from the picker's search box. It is sent only
    // when he typed one, and the server then searches it verbatim with NO query-writer model call.
    async imageSuggest(cardId, query) {
      const r = await req("POST", "/api/cards/image-suggest", query ? { cardId, query } : { cardId });
      if (r.ok && r.data) return r.data;
      // a REFUSED search (the rate limiter) has an honest reason — say it, rather than blaming the
      // providers for something the server decided
      if (r.data?.error) return { refused: r.data.error };
      return { query: "", suggestions: [], providerErrors: [{ provider: "server", kind: "http" }] };
    },
    imageAttach: (cardId, image) => req("POST", "/api/cards/image-attach", { cardId, image }),
    imageDetach: (cardId) => req("POST", "/api/cards/image-detach", { cardId }),
    async list() {
      const r = await req("GET", "/api/cards");
      return r.ok && r.data && Array.isArray(r.data.cards) ? r.data.cards : [];
    },
    generate: (notes) => req("POST", "/api/study/generate", { notes }),
    // C·3/C·4 — capture from a photo or a PDF of his notes (client downscales the image; reads the PDF
    // to base64). Optional notes ride along as focus guidance. Save stays the pending-export queue path.
    generateImage: (image, notes) => req("POST", "/api/study/generate", notes ? { image, notes } : { image }),
    generatePdf: (pdf, notes) => req("POST", "/api/study/generate", notes ? { pdf, notes } : { pdf }),
    save: (cards) => req("POST", "/api/cards", { cards }),
    // PC·2: near-duplicate guard over his flat queue (Anki dedupes exact; this catches paraphrases)
    checkDuplicates: (cards) => req("POST", "/api/cards/check-duplicates", { cards }),
    // BIG·6b: remove a SELECTION from the queue. One request carries the whole set — a chapter can
    // mint 150+ cards, and 47 separate deletes from a client loop is 47 chances to half-finish.
    remove: (cardIds) => req("POST", "/api/cards/delete", { cardIds }),
  },

  // DY·4: export bookkeeping — Anki confirmed these ids; the server flips their flags.
  anki: {
    exported: (cardIds) => req("POST", "/api/anki/exported", { cardIds }),
  },

  // DY·2: the course & assignment hub — core factory routes over two change-log collections.
  hub: {
    async courses() {
      const r = await req("GET", "/api/courses");
      return r.ok && r.data && Array.isArray(r.data.courses) ? r.data.courses : [];
    },
    createCourse: (name, term, color) => req("POST", "/api/courses", color ? { name, term, color } : { name, term }),
    updateCourse: (id, fields) => req("POST", "/api/courses/update", { id, ...fields }),
    deleteCourse: (id, cascade) => req("POST", "/api/courses/delete", cascade ? { id, cascade: true } : { id }),
    // ARC·1 — archive / unarchive ride the course resource; `assignments({ active })` asks the
    // QUERY to leave an archived course's records out (the daily surfaces), the bare list stays whole.
    archiveCourse: (id) => req("POST", "/api/courses/archive", { id }),
    unarchiveCourse: (id) => req("POST", "/api/courses/unarchive", { id }),
    async assignments({ active = false } = {}) {
      const r = await req("GET", active ? "/api/assignments?active=1" : "/api/assignments");
      return r.ok && r.data && Array.isArray(r.data.assignments) ? r.data.assignments : [];
    },
    createAssignment: (courseId, title, due) => req("POST", "/api/assignments", { courseId, title, due }),
    updateAssignment: (id, fields) => req("POST", "/api/assignments/update", { id, ...fields }),
    deleteAssignment: (id) => req("POST", "/api/assignments/delete", { id }),
  },

  // Y·4: the syllabus intake (ported from Alivia's Y·3). `parsePdf` reads a PDF into a validated
  // preview (PREVIEW ONLY, nothing persists); `save` posts the CONFIRMED preview to the hub —
  // course/assignments/exams, NEVER srs (his schedule surface is Anki).
  syllabus: {
    parsePdf: (pdf, notes) => req("POST", "/api/syllabus/parse", notes ? { pdf, notes } : { pdf }),
    save: (preview) => req("POST", "/api/syllabus/save", preview),
  },

  // NTS·5: study notes. `generate` is PREVIEW-ONLY (nothing persists) and takes the SAME source
  // union capture takes; `draftCards` asks the model to also map each card to a section, which
  // becomes each card's noteRef on save. Notes live IN-APP for him — they are not study data, and
  // nothing here touches the Anki pipeline (a noteRef is never exported to an Anki field).
  notes: {
    generate: (source, draftCards) => req("POST", "/api/notes/generate",
      draftCards && draftCards.length ? { ...source, draftCards } : { ...source }),
    save: (note) => req("POST", "/api/notes", note),
    async list() {
      const r = await req("GET", "/api/notes");
      return r.ok && r.data && Array.isArray(r.data.notes) ? r.data.notes : [];
    },
    remove: (id) => req("POST", "/api/notes/delete", { id }),
  },

  // PMT·1 — prompt presets: saved focus guidance he can pick into either guidance box.
  presets: {
    async list() {
      const r = await req("GET", "/api/presets");
      return r.ok && r.data && Array.isArray(r.data.presets) ? r.data.presets : [];
    },
    create: (title, body) => req("POST", "/api/presets", { title, body }),
    update: (id, fields) => req("POST", "/api/presets/update", { id, ...fields }),
    remove: (id) => req("POST", "/api/presets/delete", { id }),
  },

  // BIG·5b·1 — the CHAPTER LIBRARY. A whole chapter is uploaded ONCE and kept, then turned into
  // cards by a background job (BIG·5b·2 adds that button; this slice manages sources only).
  // Deliberately NOT the capture path: `/api/study/generate` is one request/one reply over one
  // page's worth of material, while this stores a source the server reads section by section.
  library: {
    async list() {
      const r = await req("GET", "/api/library");
      // normalize hard: a generic fetch stub (jsdom) may answer a shape with neither key
      return {
        sources: r.ok && r.data && Array.isArray(r.data.sources) ? r.data.sources : [],
        usage: r.ok && r.data && r.data.usage ? r.data.usage : null,
      };
    },
    upload: (pdf, filename) => req("POST", "/api/library/upload", { pdf, filename }),
    remove: (id) => req("POST", "/api/library/delete", { id }),
    // SF·1 — the chapter's own bytes, for the figure cropper. A 404 is a state (deleted source), not
    // a throw: the viewer says so and the card keeps its page reference.
    async file(id) {
      const res = await fetch(`/api/library/file?id=${encodeURIComponent(id)}`, { credentials: "include" });
      if (!res.ok) return { ok: false, status: res.status };
      return { ok: true, bytes: new Uint8Array(await res.arrayBuffer()) };
    },
  },

  // BIG·5b·2 — the chapter JOB: start (survey + park at the consent gate), go (release it), watch,
  // stop. `active()` asks with NO id, which is what lets a refresh reattach to work in flight.
  jobs: {
    async active() {
      const r = await req("GET", "/api/jobs/status");
      return r.ok && r.data && r.data.job ? r.data.job : null;
    },
    async status(id) {
      const r = await req("GET", `/api/jobs/status?id=${encodeURIComponent(id)}`);
      return r.ok && r.data && r.data.job ? r.data.job : null;
    },
    // GDE·1 — optional focus guidance rides ONLY when he typed some: an empty box sends the same
    // body as before, byte for byte.
    startChapter: (libraryId, guidance) => req("POST", "/api/jobs/start-chapter", guidance ? { libraryId, guidance } : { libraryId }),
    runChapter: (id) => req("POST", "/api/jobs/run-chapter", { id }),
    abort: (id) => req("POST", "/api/jobs/abort", { id }),
  },

  // SCH·4: the class-schedule intake (Dylan is the primary user — his D1 timetable is the fixture).
  // `parseText` is the free accessible-text path; `parsePdf` reads a grid; `save` writes `meetings`,
  // records + arithmetic, NEVER srs (his review surface is Anki).
  schedule: {
    parsePdf: (pdf, notes) => req("POST", "/api/schedule/parse", notes ? { pdf, notes } : { pdf }),
    parseText: (notes) => req("POST", "/api/schedule/parse", { notes }),
    save: (preview) => req("POST", "/api/schedule/save", preview),
    async meetings() {
      const r = await req("GET", "/api/sync/pull?cursor=0");
      const changes = r.ok && r.data && Array.isArray(r.data.changes) ? r.data.changes : [];
      return changes.filter((c) => c.collection === "meetings" && !c.deleted).map((c) => ({ id: c.id, ...c.payload }));
    },
  },

  // E·4: exam awareness — the COUNTDOWN + exm_ CRUD only. He gets no ramp (he reviews in Anki, has
  // zero srs); `upcoming` reads only the exams collection + the clock, never a schedule.
  exams: {
    async list() {
      const r = await req("GET", "/api/exams");
      return r.ok && r.data && Array.isArray(r.data.exams) ? r.data.exams : [];
    },
    async upcoming() {
      const r = await req("GET", "/api/exams/upcoming");
      return r.ok && r.data && Array.isArray(r.data.exams) ? r.data.exams : [];
    },
    create: (courseId, title, date, type) => req("POST", "/api/exams", { courseId, title, date, ...(type ? { type } : {}) }),
    update: (id, fields) => req("POST", "/api/exams/update", { id, ...fields }),
    remove: (id) => req("POST", "/api/exams/delete", { id }),
  },

  // CAL·6b — personal / after-school events (pev_). `list` reads them for the calendar (degrades to
  // [] like api.hub.assignments()); create/update/remove are the write wrappers. No srs anywhere here.
  events: {
    async list() {
      const r = await req("GET", "/api/events");
      return r.ok && r.data && Array.isArray(r.data.events) ? r.data.events : [];
    },
    create: (evt) => req("POST", "/api/events", evt),
    update: (id, fields) => req("POST", "/api/events/update", { id, ...fields }),
    remove: (id) => req("POST", "/api/events/delete", { id }),
  },

  // A·3: the hour log (ported from Alivia's A·1) — clinical/shadowing hours, documented as they
  // happen. The active timer is SERVER-side (a singleton record), so it follows his devices.
  // Records + arithmetic only: nothing here touches his srs-free seam or Anki.
  hours: {
    async list() {
      const r = await req("GET", "/api/hours");
      return r.ok && r.data && Array.isArray(r.data.entries) ? r.data.entries : [];
    },
    async active() {
      const r = await req("GET", "/api/hours/active");
      return r.ok && r.data && r.data.active ? r.data.active : null;
    },
    start: (category, place, activity) =>
      req("POST", "/api/hours/start", { category, place, ...(activity ? { activity } : {}) }),
    stop: () => req("POST", "/api/hours/stop", {}),
    add: (fields) => req("POST", "/api/hours/add", fields),
    update: (id, fields) => req("POST", "/api/hours/update", { id, ...fields }),
    remove: (id) => req("POST", "/api/hours/delete", { id }),
  },
};

// A·3 — the injectable clock (the srs tests' discipline, client-side): jsdom stubs window.__now
// so the ticking elapsed display is asserted from a fixed instant, never real time.
const nowMs = () => (typeof window !== "undefined" && window.__now ? window.__now() : Date.now());
/** Elapsed ms -> "h:mm:ss" (or "m:ss" under an hour) for the live timer readout. */
function fmtElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const pad = (n) => String(n).padStart(2, "0");
  const h = Math.floor(s / 3600);
  return h > 0 ? `${h}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}` : `${Math.floor(s / 60)}:${pad(s % 60)}`;
}
/** Stored minutes -> "2h 15m" / "45m" for totals and entry rows. */
function fmtMins(m) {
  if (m < 60) return `${m}m`;
  return m % 60 === 0 ? `${m / 60}h` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

// --- the one signature element: the wordmark + DDS chip (styled in index.html) ---
function Wordmark() {
  return <span className="wordmark">Dylan<span className="dds" aria-hidden="true">DDS</span></span>;
}

// F·2: map an auth failure to honest, kid-appropriate copy by STATUS (not fragile string-matching),
// preferring the server's own text on a 400 (it carries the real validation bound — e.g. the
// password length). A thrown fetch is handled at the call site as "couldn't reach the server".
function authError(registering, r) {
  const server = r.data && typeof r.data.error === "string" ? r.data.error.trim() : "";
  // F2·1: prefer the server text on 429 too — it carries the honest lockout minutes (S2's contract);
  // without this the lockout rendered the generic line and read as a silent dead-end in the field.
  if ((r.status === 400 || r.status === 429) && server) return server;
  if (r.status === 429) return "Too many attempts — wait a few minutes and try again.";
  if (registering) {
    if (r.status === 409) return "That email already has an account — sign in instead.";
    if (r.status === 403) return "Sign-ups are closed here — this app is already yours. Ask Dad if you're locked out.";
  } else if (r.status === 401) {
    return "That email and password don't match.";
  }
  return "That didn't work — check your details and try again.";
}

function AuthScreen({ onSignedIn }) {
  const [mode, setMode] = useState("signin"); // "signin" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const registering = mode === "register";

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) { setError("Enter an email and a password."); return; }
    setBusy(true);
    let r;
    try {
      r = registering
        ? await api.createAccount(email.trim(), password)
        : await api.signIn(email.trim(), password);
    } catch {
      setBusy(false);
      setError("Couldn't reach the server — nothing changed. Check your connection and retry.");
      return;
    }
    setBusy(false);
    if (r.ok && r.data) { onSignedIn(r.data.user); return; }
    setError(authError(registering, r));
  }

  function swap(next) { setMode(next); setError(""); }

  return (
    <main className="auth">
      <form className="card" onSubmit={submit} noValidate>
        <Wordmark />
        <p className="tagline">Your dental-school companion.</p>

        <label className="field">
          <span>Email</span>
          <input type="email" autoComplete="email" inputMode="email"
            value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>Password</span>
          <input type="password" autoComplete={registering ? "new-password" : "current-password"}
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Just a moment…" : registering ? "Create account" : "Sign in"}
        </button>

        <p className="switch">
          {registering
            ? <>Already have an account? <button type="button" className="link" onClick={() => swap("signin")}>Sign in</button></>
            : <>New here? <button type="button" className="link" onClick={() => swap("register")}>Create an account</button></>}
        </p>
      </form>
    </main>
  );
}

// The D1 dashboard. Courses/GPA/cards/Anki go live over DY·2 → DY·4; the last three tiles are
// honest placeholders for the D1→D4 arc (sim lab, clinic, boards) — planned, not pretended.
const TILES = [
  { id: "courses", title: "Courses & Assignments", body: "Every course, lab block, and due date in one place.", hint: "Add your D1 fall schedule to begin." },
  { id: "calendar", title: "Calendar", body: "Courses, assignments, exams, and your own plans on a month grid." },
  { id: "gpa", title: "GPA", stat: "—", statLabel: "cumulative GPA", body: "Grades roll up here as you enter them." },
  { id: "cards", title: "Study & Cards", body: "Paste lecture notes, generate cards, vet them — your queue for Anki." },
  { id: "anki", title: "Anki", body: "Push vetted cards to Anki Desktop; review on any device." },
  { id: "hours", title: "Hours Log", body: "Clinical and shadowing hours — documented as they happen.", hint: "Start a timer or add an entry." },
  { id: "notes", title: "Study Notes", body: "Organized notes from anything you capture — they stay here when the cards go to Anki.", hint: "Notes orient you; cards make it stick." },
  { id: "simlab", title: "Sim Lab", body: "Preclinical practice, logged.", hint: "Logging lands with D1." },
  { id: "clinical", title: "Clinical Requirements", body: "Requirement counts by coded patient — never PHI.", hint: "Opens when clinic starts, D2." },
  { id: "inbde", title: "INBDE", body: "Boards prep on the Foundation Knowledge × Clinical Content map.", hint: "D3 — the FK×CC map lands before boards." },
  { id: "library", title: "Chapter Library", body: "Whole chapters, stored and ready to turn into cards.", hint: "Capture is one page; a chapter is the whole thing." },
  { id: "guide", title: "Guide", body: "New here? How each part works, in plain language.", hint: "Start of semester — read me first." },
];

function Tile({ title, body, hint, stat, statLabel, badge, onClick }) {
  const inner = (
    <>
      <div className="tile-head">
        <h2>{title}</h2>
        {badge > 0 && <span className="tile-badge">{badge}</span>}
      </div>
      {stat !== undefined && (
        <p className="stat"><span className="stat-num">{stat}</span><span className="stat-label">{statLabel}</span></p>
      )}
      <p className="tile-body">{body}</p>
      {hint && <p className="tile-hint">{hint}</p>}
    </>
  );
  return onClick
    ? <button type="button" className="tile tile-button" onClick={onClick}>{inner}</button>
    : <section className="tile">{inner}</section>;
}

// DY·2 — the D1→D4 phase marker: a genuine four-year sequence, planted now so the future
// per-phase tile re-weighting has a home. Purely informational (no gamification).
const DPHASES = ["D1", "D2", "D3", "D4"];

function PhaseMarker() {
  return (
    <div className="dphase" role="img" aria-label="Program phase: D1 of D4">
      {DPHASES.map((p) => (
        <span key={p} className={p === "D1" ? "dphase-step is-active" : "dphase-step"}>{p}</span>
      ))}
    </div>
  );
}

// A·3 — the "Today" panel (ported from Alivia's A·2): what should I touch today, computed
// CLIENT-side from already-synced hub data (computePacing — pure arithmetic, zero new endpoints).
function TodayPanel({ asns, courses, onOpenCourses }) {
  const { items, more } = computePacing(asns, localDateStr());
  const courseName = Object.fromEntries(courses.map((c) => [c.id, c.name]));
  return (
    <section className="today">
      <h2>Today</h2>
      {items.length === 0
        ? <p className="muted">Nothing pending — you&apos;re ahead.</p>
        : <ul className="today-list">
            {items.map((it) => (
              <li key={it.id} className={`today-row is-${it.status}`}>
                <span className="today-title">{it.title}</span>
                {courseName[it.courseId] && <span className="today-course">{courseName[it.courseId]}</span>}
                <span className={`today-left${it.status === "overdue" ? " overdue" : ""}`}>{it.label}</span>
                {it.nudge && <span className="today-nudge">{it.nudge}</span>}
              </li>
            ))}
          </ul>}
      {more > 0 && <button className="link" onClick={onOpenCourses}>+ {more} more in Courses</button>}
    </section>
  );
}

// E·4 — the exam countdown, beside his Today panel (ported from Alivia's E·3). Nearest exams, days
// until, sorted; QUIET when empty. Reads server-computed daysUntil from /api/exams/upcoming — no srs,
// so it stands alone for his zero-schedule app. In-window exams are flagged. NO ramp: he reviews in
// Anki; the app is his hub for exams/assignments, and the countdown is the whole exam-awareness surface.
function ExamCountdown({ exams, onOpenCourses }) {
  if (!exams || exams.length === 0) return null; // quiet when empty
  const when = (d) => (d === 0 ? "today" : d === 1 ? "tomorrow" : `in ${d} days`);
  return (
    <section className="exam-countdown">
      <h2>Exams</h2>
      <ul className="exam-count-list">
        {exams.slice(0, 5).map((e) => (
          <li key={e.id} className={`exam-count-row${e.daysUntil <= RAMP_WINDOW_DAYS ? " is-soon" : ""}`}>
            <span className="exam-count-title">{e.title}</span>
            <span className="exam-count-when">{when(e.daysUntil)}</span>
          </li>
        ))}
      </ul>
      {exams.length > 5 && <button className="link" onClick={onOpenCourses}>+ {exams.length - 5} more in Courses</button>}
    </section>
  );
}

function Dashboard({ onOpenLibrary, onOpenCourses, onOpenCalendar, onOpenCards, onOpenAnki, onOpenHours, onOpenGuide, onOpenNotes }) {
  const [hub, setHub] = useState(null); // { courses, asns } for the live tile counts
  const [cards, setCards] = useState(null); // DY·3: the capture queue, for the pending badge
  const [ankiStat, setAnkiStat] = useState(null); // DY·4: null loading | { connected, reviewed }
  const [timer, setTimer] = useState(null); // A·3: the running-timer chip — he can't forget it
  const [exams, setExams] = useState(null); // E·4: the upcoming-exam countdown
  useEffect(() => {
    let alive = true;
    // ARC·1 — the Today panel + the Courses tile are daily surfaces: active courses' assignments only
    Promise.all([api.hub.courses(), api.hub.assignments({ active: true })])
      .then(([courses, asns]) => { if (alive) setHub({ courses, asns }); });
    api.hours.active().then((t) => { if (alive) setTimer(t); });
    api.exams.upcoming().then((x) => { if (alive) setExams(x); });
    api.cards.list().then((c) => { if (alive) setCards(c); });
    anki.hello().then(async (h) => {
      if (!h.ok) { if (alive) setAnkiStat({ connected: false }); return; }
      const r = await anki.reviewedToday();
      if (alive) setAnkiStat({ connected: true, reviewed: r.ok ? r.count : 0 });
    });
    return () => { alive = false; };
  }, []);
  // DY·4: the Anki tile — the live stat when AnkiConnect answers, the honest hint otherwise
  const ankiConnected = ankiStat?.connected === true;
  const ankiHint = ankiStat == null ? "Loading…"
    : ankiConnected ? "Connected." : "Not connected — open to set up.";
  // DY·3: the Study & Cards tile — pending export (saved but not yet confirmed in Anki)
  const pending = cards ? cards.filter((c) => c.exported !== true).length : null;
  const cardsHint = pending == null ? "Loading…"
    : pending === 0 ? "Nothing waiting for Anki."
    : `${pending} card${pending === 1 ? "" : "s"} waiting for Anki.`;
  // live tile counts: due this week (7 days) + overdue, over open assignments in every term
  const today = localDateStr();
  const week = addDays(7);
  const openAsns = hub ? hub.asns.filter((a) => !a.done) : [];
  const dueWeek = openAsns.filter((a) => a.due >= today && a.due <= week).length;
  const overdueCount = openAsns.filter((a) => a.due < today).length;
  const coursesHint = hub == null ? "Loading…"
    : hub.courses.length === 0 ? "Add your D1 fall schedule to begin."
    : overdueCount > 0 ? `${dueWeek} due this week · ${overdueCount} overdue`
    : dueWeek > 0 ? `${dueWeek} due this week` : "All clear this week.";
  // the GPA tile — cumulative across all terms present, "—" until a grade exists
  const gpaStat = hub ? fmtGpa(gpaOf(hub.courses)) : "—";

  return (
    <div className="dash">
      <header className="dash-head">
        <div className="dash-when">
          <h1>Fall 2026</h1>
          <p className="phase">UNMC College of Dentistry</p>
        </div>
        <div className="dash-chips">
          {timer && (
            <button className="timer-chip" onClick={onOpenHours}
              title="An hours timer is running — tap to open the log.">
              ⏱ {timer.activity || timer.category} · running
            </button>
          )}
          <PhaseMarker />
        </div>
      </header>
      {hub && <TodayPanel asns={hub.asns} courses={hub.courses} onOpenCourses={onOpenCourses} />}
      {exams && <ExamCountdown exams={exams} onOpenCourses={onOpenCourses} />}
      <div className="tiles">
        {TILES.map(({ id, ...rest }) =>
          id === "courses"
            ? <Tile key={id} {...rest} badge={dueWeek + overdueCount} hint={coursesHint} onClick={onOpenCourses} />
            : id === "calendar"
            ? <Tile key={id} {...rest} onClick={onOpenCalendar} />
            : id === "gpa"
              ? <Tile key={id} {...rest} stat={gpaStat} onClick={onOpenCourses} />
              : id === "cards"
                ? <Tile key={id} {...rest} badge={pending} hint={cardsHint} onClick={onOpenCards} />
                : id === "anki"
                  ? <Tile key={id} {...rest} hint={ankiHint} onClick={onOpenAnki}
                      stat={ankiConnected ? String(ankiStat.reviewed) : undefined}
                      statLabel={ankiConnected ? "reviewed today" : undefined} />
                  : id === "hours"
                    ? <Tile key={id} {...rest} hint={timer ? "Timer running now." : rest.hint} onClick={onOpenHours} />
                    : id === "notes"
                      ? <Tile key={id} {...rest} onClick={onOpenNotes} />
                    : id === "library"
                      ? <Tile key={id} {...rest} onClick={onOpenLibrary} />
                    : id === "guide"
                      ? <Tile key={id} {...rest} onClick={onOpenGuide} />
                      : <Tile key={id} {...rest} />
        )}
      </div>
    </div>
  );
}

// --- DY·3: Study & Cards — paste notes -> AI preview (server-authoritative, not yet saved) ->
// vet/edit -> save into the PENDING-EXPORT QUEUE. Below the capture form, the queue itself:
// every saved card without the `exported` flag, waiting for the Anki push (DY·4).
const MAX_NOTES = 8000; // mirrors the server cap so the counter warns before the request is rejected
const MAX_EDGE = 1568;  // downscale long edge — the model reads plenty of detail at this size

/**
 * C·3 — client-side downscale + re-encode before upload: long edge <= MAX_EDGE, JPEG q~0.82, ALWAYS
 * output image/jpeg (normalizes iPhone HEIC where the browser can decode it), EXIF orientation honored.
 * ISOLATED + injectable so jsdom (no canvas) stubs its output via window.__downscaleImage; the real
 * canvas path is exercised by construction, not by the offline suite. Throws on an undecodable file
 * (e.g. HEIC on a Chromebook) so the caller can show the honest "try a JPG or PNG" copy.
 * @returns {Promise<{ mediaType: string, data: string }>}  base64 (no data: prefix)
 */
async function downscaleImage(file) {
  if (typeof window !== "undefined" && window.__downscaleImage) return window.__downscaleImage(file);
  let bmp, w, h;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    w = bmp.width; h = bmp.height;
  } catch {
    const img = await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const el = new Image();
      el.onload = () => { URL.revokeObjectURL(url); resolve(el); };
      el.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
      el.src = url;
    });
    bmp = img; w = img.naturalWidth; h = img.naturalHeight;
  }
  const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale); canvas.height = Math.round(h * scale);
  canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
  return { mediaType: "image/jpeg", data: canvas.toDataURL("image/jpeg", 0.82).split(",")[1] };
}

/**
 * C·4 — read a PDF file to base64 (no downscale; the server caps decoded size). Isolated +
 * injectable via window.__readPdf so jsdom stubs its output. Throws on a read failure.
 * @returns {Promise<{ mediaType: string, data: string }>}
 */
async function readPdf(file) {
  if (typeof window !== "undefined" && window.__readPdf) return window.__readPdf(file);
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error("read failed"));
    fr.readAsDataURL(file);
  });
  return { mediaType: "application/pdf", data: String(dataUrl).split(",")[1] };
}

/** Approx decoded byte length of a base64 string — for the attached-file size chip. */
function b64Bytes(data) {
  const pad = data.endsWith("==") ? 2 : data.endsWith("=") ? 1 : 0;
  return Math.floor((data.length * 3) / 4) - pad;
}
function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  // BIG·5b·1 — a chapter library is measured in GB, and "4096.0 MB" is not a number anyone reads.
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// NTS·5 — the honest framing, verbatim wherever notes appear. It reads doubly true for him: his
// cards leave for Anki, so the notes are the one thing that stays in-app to orient him.
const NOTES_TAGLINE = "Notes orient you; cards make it stick.";

// IMG·4 — images on cards, his half. The locked design is unchanged and enforced server-side: the
// app NEVER GENERATES an image, it recommends licensed ones, and HIS TAP is the only thing that
// attaches one. What is his alone is the delivery: an attached image rides the AnkiConnect push as a
// stored media file referenced from the note (src/anki.js), because his review surface is Anki.
// Still ZERO srs — an image is a field on a card, and a card here is a pending export, not a schedule.

/**
 * Fetch an ALLOWLISTED image URL and downscale it to a data URI, ready to attach. Isolated +
 * injectable via window.__fetchAndDownscale so jsdom (no canvas, no network) stubs it — the
 * `downscaleImage` precedent. Throws on a fetch or decode failure so the caller shows honest copy.
 */
async function fetchAndDownscale(url) {
  if (typeof window !== "undefined" && window.__fetchAndDownscale) return window.__fetchAndDownscale(url);
  // IMG·S·4 — redirects refused, and the FINAL url re-checked against the allowlist: an allowed
  // host that 302s elsewhere must never be where the bytes actually came from.
  const res = await fetch(url, { redirect: "error" });
  if (!res.ok) throw new Error("could not fetch the image");
  if (res.url && !isAllowedImageUrl(res.url)) throw new Error("image moved off the allowed hosts");
  const { mediaType, data } = await downscaleImage(await res.blob());
  return `data:${mediaType};base64,${data}`;
}

/**
 * IMG·Q·3 — mean luminance (0..1) of the image at `url`, or null when it cannot be measured.
 * Injectable via window.__measureLuminance so jsdom (no canvas) stubs it — the fetchAndDownscale
 * precedent.
 *
 * Measured on a SEPARATE offscreen image, never the grid's own <img>: reading pixels needs a
 * CORS-clean canvas (`crossOrigin`), and making the VISIBLE thumbnail depend on a CORS handshake
 * would risk the whole grid to police a corner case. So a measurement that cannot happen — no
 * canvas, a tainted read, a refused cross-origin load — returns null and the candidate is KEPT.
 * Fail open: a picture is never dropped because we could not look at it.
 */
async function measureLuminance(url) {
  if (typeof window !== "undefined" && window.__measureLuminance) return window.__measureLuminance(url);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = "anonymous";
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("could not load for measurement"));
      el.src = url;
    });
    const n = LUMINANCE_SAMPLE_PX;
    const canvas = document.createElement("canvas");
    canvas.width = n; canvas.height = n;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, n, n);
    const { data: px } = ctx.getImageData(0, 0, n, n);
    let sum = 0;
    for (let i = 0; i < px.length; i += 4) {
      sum += (0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2]) / 255;
    }
    return sum / (px.length / 4);
  } catch {
    return null; // unmeasurable is not a verdict
  }
}

/** The image on a pending row, with the credit that licenses it. */
// ── SF·1 — SOURCE FIGURES, Design A: manual crop, ZERO model tokens ──────────────────────────────
// A chapter card carries sourceRef (chapter + pages). "Add figure" opens those pages in the browser,
// he drags a box around the actual diagram, and the crop becomes the card's image — through the
// EXISTING image pipeline (the attach route's cap and type allowlist, provider "manual", the drop-the-
// key detach, the Anki media push). FIG·1 made the cards DESCRIBE diagrams; this puts the diagram on
// the card. No model is involved anywhere on this path, and sourceRef itself is never modified.
//
// TWO SEAMS, because jsdom can run neither pdf.js (no canvas, no workers) nor canvas encoding: the
// renderer (`window.__pdfRenderer`) and the crop-to-image step (`window.__cropImage`). The suite
// pins the FLOW around them — open at sourceRef.pageStart, page navigation, drag → box → attach.
const FIGURE_RENDER_SCALE = 2; // render at 2× so a crop keeps its detail after the downscale
const CROP_MIN_PX = 8;

/** Open a PDF for page rendering. Real: pdf.js (bundled), its worker served beside app.js. */
async function openPdfRenderer(bytes) {
  if (typeof window !== "undefined" && window.__pdfRenderer) return window.__pdfRenderer.open(bytes);
  const pdfjs = await import("pdfjs-dist/build/pdf.mjs"); // lazy: nothing of pdf.js runs until a figure is opened
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
  const doc = await pdfjs.getDocument({ data: bytes }).promise;
  return {
    pages: doc.numPages,
    async render(pageNumber) {
      const page = await doc.getPage(pageNumber);
      const viewport = page.getViewport({ scale: FIGURE_RENDER_SCALE });
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      return canvas;
    },
    close() { try { doc.destroy(); } catch { /* already gone */ } },
  };
}

/**
 * Crop a page canvas to a box and encode it UNDER the card-image cap: jpeg, then shrink the box's
 * pixel size by 20% and ease the quality a step, up to eight times. A crop that still will not fit
 * is refused with copy that names the cap — never a silent oversize attach (the route would refuse
 * it anyway; this just says why before the round trip).
 */
async function cropToCardImage(canvas, rect) {
  if (typeof window !== "undefined" && window.__cropImage) return window.__cropImage(canvas, rect);
  let w = Math.max(1, Math.round(rect.w)), h = Math.max(1, Math.round(rect.h));
  let quality = 0.85;
  for (let attempt = 0; attempt < 8; attempt++) {
    const out = document.createElement("canvas");
    out.width = w; out.height = h;
    out.getContext("2d").drawImage(canvas, rect.x, rect.y, rect.w, rect.h, 0, 0, w, h);
    const data = out.toDataURL("image/jpeg", quality).split(",")[1];
    if (b64Bytes(data) <= CARD_IMAGE_MAX_BYTES) return { mediaType: "image/jpeg", data };
    w = Math.max(1, Math.round(w * 0.8)); h = Math.max(1, Math.round(h * 0.8)); quality = Math.max(0.6, quality - 0.05);
  }
  throw new Error(`That crop can't be squeezed under the ${Math.round(CARD_IMAGE_MAX_BYTES / 1024)}KB image cap even after shrinking — try a tighter box around just the figure.`);
}

function FigureCropper({ card, source, onAttached, onClose }) {
  const ref = card.sourceRef;
  const [phase, setPhase] = useState("loading"); // loading | ready | gone | error
  const [page, setPage] = useState(ref.pageStart);
  const [pages, setPages] = useState(0);
  const [sel, setSel] = useState(null);   // { x, y, w, h } in CANVAS pixels
  const dragRef = useRef(null);           // the anchor while the mouse is down — a ref, so a mousemove that lands before React flushes the mousedown still sees it
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const stageRef = useRef(null);
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await api.library.file(ref.libraryId);
      if (!alive) return;
      if (!r.ok) { setPhase(r.status === 404 ? "gone" : "error"); return; }
      try {
        const renderer = await openPdfRenderer(r.bytes);
        if (!alive) { renderer.close?.(); return; }
        rendererRef.current = renderer;
        setPages(renderer.pages);
        setPage(Math.min(Math.max(1, ref.pageStart), renderer.pages)); // open where the card came from
        setPhase("ready");
      } catch { if (alive) setPhase("error"); }
    })();
    return () => { alive = false; rendererRef.current?.close?.(); };
  }, [ref.libraryId]);

  useEffect(() => {
    if (phase !== "ready" || !rendererRef.current) return undefined;
    let alive = true;
    setSel(null); dragRef.current = null;
    rendererRef.current.render(page).then((canvas) => {
      if (!alive || !hostRef.current) return;
      canvasRef.current = canvas;
      hostRef.current.replaceChildren(canvas); // the canvas is pdf.js's; React owns nothing inside the host
    }).catch(() => { if (alive) setErr("Couldn't draw that page."); });
    return () => { alive = false; };
  }, [phase, page]);

  // CSS px → canvas px. jsdom reports a 0-wide stage, so the fallback keeps the scale at 1 there.
  function toCanvas(e) {
    const stage = stageRef.current, canvas = canvasRef.current;
    if (!stage || !canvas) return null;
    const r = stage.getBoundingClientRect();
    const k = canvas.width / (r.width || canvas.width);
    return { x: Math.max(0, (e.clientX - r.left + stage.scrollLeft) * k), y: Math.max(0, (e.clientY - r.top + stage.scrollTop) * k), k };
  }
  function down(e) { const p = toCanvas(e); if (!p) return; e.preventDefault(); dragRef.current = p; setSel(null); setErr(""); }
  function move(e) {
    const drag = dragRef.current;
    if (!drag) return;
    const p = toCanvas(e); if (!p) return;
    setSel({ x: Math.min(drag.x, p.x), y: Math.min(drag.y, p.y), w: Math.abs(p.x - drag.x), h: Math.abs(p.y - drag.y) });
  }
  function up(e) { if (!dragRef.current) return; move(e); dragRef.current = null; }
  const usable = !!sel && sel.w >= CROP_MIN_PX && sel.h >= CROP_MIN_PX;

  async function useCrop() {
    if (!usable || busy) return;
    setBusy(true); setErr("");
    try {
      const { mediaType, data } = await cropToCardImage(canvasRef.current, sel);
      const r = await api.cards.imageAttach(card.id, { dataUri: `data:${mediaType};base64,${data}`, provider: "manual" });
      if (!r.ok) { setErr(r.data?.error || "Couldn't attach that crop."); setBusy(false); return; }
      onAttached(r.data.image);
    } catch (e) {
      setErr(e?.message || "Couldn't make an image from that crop.");
      setBusy(false);
    }
  }

  const k = canvasRef.current ? (stageRef.current?.getBoundingClientRect().width || canvasRef.current.width) / canvasRef.current.width : 1;
  return (
    <section className="figure-viewer" aria-label="Crop a figure from the chapter">
      <div className="figure-nav">
        <strong>Figure from {source ? source.filename : "this chapter"}</strong>
        {phase === "ready" && <>
          <button type="button" className="link" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</button>
          <span className="figure-page-label">Page {page} of {pages}</span>
          <button type="button" className="link" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>Next →</button>
        </>}
        <button type="button" className="link" onClick={onClose}>Close</button>
      </div>
      {phase === "loading" && <p className="muted">Opening the chapter…</p>}
      {phase === "gone" && <p className="muted figure-gone">This chapter was deleted from your library, so its pages can&apos;t be opened. The card and its page reference are kept.</p>}
      {phase === "error" && <p className="error" role="alert">Couldn&apos;t open that chapter&apos;s pages.</p>}
      {phase === "ready" && <>
        <p className="muted">Drag a box around the figure, then use the crop. It becomes the card&apos;s image, exactly like an uploaded one.</p>
        <div className="figure-stage" ref={stageRef} onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}>
          <div className="figure-canvas" ref={hostRef} />
          {sel && <div className="figure-select" style={{ left: sel.x * k, top: sel.y * k, width: sel.w * k, height: sel.h * k }} />}
        </div>
        <div className="cardform-actions">
          <button type="button" className="btn btn-sm figure-use" disabled={!usable || busy} onClick={useCrop}>{busy ? "Cropping…" : "Use this crop"}</button>
          {card.image && <span className="muted">This replaces the card&apos;s current image.</span>}
        </div>
      </>}
      {err && <p className="error" role="alert">{err}</p>}
    </section>
  );
}
// ── /SF·1 ────────────────────────────────────────────────────────────────────────────────────────

function CardImage({ image }) {
  if (!image || !image.dataUri) return null;
  return (
    <figure className="card-image">
      <img src={image.dataUri} alt={image.title || ""} />
      <figcaption className="card-image-credit">
        {image.provider === "manual"
          ? "Your photo"
          : <>{image.author || "Unknown author"} · {image.license}
              {image.source && <> · <a href={image.source} target="_blank" rel="noreferrer">source</a></>}</>}
      </figcaption>
    </figure>
  );
}

/**
 * The picker: ask for suggestions, show what came back with its licence visible, attach exactly the
 * one he taps. Manual upload sits beside it as an equal, and is the honest offer when search is down.
 */
function ImagePicker({ card, onAttached, onClose }) {
  const [state, setState] = useState({ phase: "idle" }); // idle | loading | done | deferred | refused
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  // IMG·F·2 — candidate-specific degradation (live-fire evidence: Openverse's proxy answers 424 for
  // a dead upstream). A failed thumb load or a failed download drops THAT candidate only.
  const [dead, setDead] = useState(() => new Set());
  const markDead = (id) => setDead((prev) => new Set(prev).add(id));
  // IMG·Q·3 — the near-black drop, riding the EXISTING per-candidate drop path (F·2). A thumbnail
  // that loads and turns out to be almost entirely black is a plate scan, not a picture of the
  // thing; it costs ITSELF a grid slot and never the grid. Unmeasurable → kept.
  const screenDark = async (hit) => {
    const lum = await measureLuminance(hit.thumbUrl);
    if (typeof lum === "number" && lum < NEAR_BLACK_LUMINANCE) markDead(hit.id);
  };
  // IMG·Q·1 — HE steers the search. The model's phrase is the PRE-FILLED DEFAULT, so the one-tap
  // flow is unchanged for anyone who doesn't care; edit the words, press Search, and the server
  // searches them verbatim — no query-writer model call at all on a retry. This matters most here:
  // clinical terms are exactly where an auto-written phrase misses.
  const [q, setQ] = useState("");
  const [searched, setSearched] = useState(false);

  async function findImages(typed) {
    setState({ phase: "loading" }); setErr(null); setSearched(true);
    const r = await api.cards.imageSuggest(card.id, typed);
    if (r.refused) { setState({ phase: "refused", message: r.refused }); return; }
    if (r.deferred) { setState({ phase: "deferred" }); return; }
    setQ(typed || r.query || "");
    setState({ phase: "done", query: r.query, suggestions: r.suggestions ?? [], providerErrors: r.providerErrors ?? [] });
  }

  /** HIS TAP, and only his tap, attaches: fetch → downscale → attach, with the provenance intact. */
  async function approve(hit) {
    setBusy(hit.id); setErr(null);
    try {
      const dataUri = await fetchAndDownscale(hit.imageUrl);
      const r = await api.cards.imageAttach(card.id, {
        dataUri, provider: hit.provider, source: hit.sourcePage, license: hit.license,
        author: hit.author, licenseUrl: hit.licenseUrl, title: hit.title,
      });
      if (!r.ok) { setErr(r.data?.error || "Couldn't attach that one — try another."); setBusy(null); return; }
      onAttached(r.data.image);
    } catch {
      // the failure belongs to THIS candidate (a dead upstream, an undecodable file) — drop it
      markDead(hit.id);
      setErr("Couldn't download that image. Try another, or upload your own.");
      setBusy(null);
    }
  }

  async function uploadOwn(file) {
    if (!file) return;
    setBusy("manual"); setErr(null);
    try {
      const { mediaType, data } = await downscaleImage(file);
      const r = await api.cards.imageAttach(card.id, { dataUri: `data:${mediaType};base64,${data}`, provider: "manual" });
      if (!r.ok) { setErr(r.data?.error || "Couldn't attach that image."); setBusy(null); return; }
      onAttached(r.data.image);
    } catch {
      setErr("Couldn't read that file — try a JPG or PNG.");
      setBusy(null);
    }
  }

  const providersDown = state.phase === "done" && state.suggestions.length === 0 && state.providerErrors.length > 0;
  // the LIVE grid: suggestions minus candidates whose thumbs failed (IMG·F·2)
  const live = state.phase === "done" ? state.suggestions.filter((h) => !dead.has(h.id)) : [];
  const allDead = state.phase === "done" && state.suggestions.length > 0 && live.length === 0;
  // IMG·QF·3 — the BLANK PANEL, closed. A search that simply found nothing (no provider failed, no
  // candidate died, the query was not empty) rendered NOTHING AT ALL: the field opened the picker,
  // searched, and got a silent empty box. Every other outcome here says what happened; so does this.
  const nothingFound = state.phase === "done" && !!state.query
    && state.suggestions.length === 0 && state.providerErrors.length === 0;
  return (
    <section className="image-picker">
      <div className="image-picker-head">
        <h3>Add an image</h3>
        <button className="link" onClick={onClose}>Close</button>
      </div>
      <div className="image-picker-actions">
        <button className="btn btn-ghost" onClick={() => findImages()} disabled={state.phase === "loading"}>
          {state.phase === "loading" ? "Looking…" : "Find an image"}
        </button>
        <label className="btn btn-ghost image-upload">
          Upload my own
          <input type="file" accept="image/*" aria-label="Upload my own image"
            onChange={(e) => uploadOwn(e.target.files && e.target.files[0])} />
        </label>
      </div>

      {/* IMG·Q·1 — the search box: his words, his retry, no model call behind it */}
      {searched && (
        <form className="image-search" onSubmit={(e) => { e.preventDefault(); if (q.trim()) findImages(q.trim()); }}>
          <input type="text" value={q} aria-label="Image search words" placeholder="Search words"
            onChange={(e) => setQ(e.target.value)} />
          <button type="submit" className="btn btn-ghost" disabled={state.phase === "loading" || !q.trim()}>Search</button>
        </form>
      )}

      {err && <p className="error" role="alert">{err}</p>}
      {state.phase === "refused" && <p className="error" role="alert">{state.message}</p>}
      {state.phase === "deferred" && (
        <p className="muted" role="status">Couldn&apos;t work out what to search for just now — try again, or upload your own.</p>
      )}
      {state.phase === "done" && state.query === "" && (
        <p className="muted" role="status">No image would really help this card. Upload your own if you disagree.</p>
      )}
      {providersDown && (
        <p className="muted" role="status">
          Image search is down right now ({state.providerErrors.map((p) => p.provider).join(", ")}) — upload your own instead.
        </p>
      )}
      {nothingFound && (
        <p className="muted" role="status">Nothing came back for those words — try different ones, or upload your own.</p>
      )}
      {allDead && (
        <p className="muted" role="status">None of the images could be loaded — try again, or upload your own.</p>
      )}
      {live.length > 0 && (
        <>
          <p className="muted image-query">Tap one to add it — nothing is attached until you do.</p>
          <ul className="image-grid">
            {live.map((hit) => (
              <li key={hit.id} className="image-hit">
                <button type="button" className="image-hit-btn" disabled={busy !== null}
                  aria-label={`Use this image: ${hit.title || hit.provider}, ${hit.license}`}
                  onClick={() => approve(hit)}>
                  {/* a thumb that fails to load drops ITS candidate only (IMG·F·2) */}
                  <img src={hit.thumbUrl} alt="" loading="lazy" onError={() => markDead(hit.id)}
                    onLoad={() => screenDark(hit)} />
                  <span className="image-hit-meta">
                    <span className="image-hit-author">{hit.author || "Unknown author"}</span>
                    <span className="image-hit-license">{hit.license}</span>
                  </span>
                  {busy === hit.id && <span className="image-hit-busy" role="status">Adding…</span>}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function CardsView({ onBack }) {
  const [notes, setNotes] = useState("");
  const [attach, setAttach] = useState(null);    // C·3/C·4: { kind: "image"|"pdf", name, mediaType, data }
  // NTS·5 — what this capture produces. "cards" is the DEFAULT and its path is byte-unchanged.
  const [product, setProduct] = useState("cards"); // "cards" | "notes" | "both"
  const [noteDraft, setNoteDraft] = useState(null); // NTS·5: { title, sections: [{ heading, content, keyTerms? }] }
  const [phase, setPhase] = useState("compose"); // "compose" | "preview"
  const [preview, setPreview] = useState([]);    // editable preview: [{ _k, front, back, tags }]
  const [queue, setQueue] = useState(null);      // the saved capture queue (null while loading)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pickingId, setPickingId] = useState(null); // IMG·4 — the pending row whose picker is open
  const [croppingId, setCroppingId] = useState(null); // SF·1 — the row whose figure cropper is open
  // BIG·6b — the delete selection: the ids ticked in the queue, and whether the confirm is open.
  // A Set, not an array: 150 rows re-render on every tick, and membership is the only question asked.
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDel, setConfirmDel] = useState(false);
  const tooLong = notes.length > MAX_NOTES;
  // An attachment makes notes optional (they ride as guidance); otherwise notes are required.
  const canGenerate = !busy && (attach ? true : Boolean(notes.trim()) && !tooLong);

  function refresh() { return api.cards.list().then(setQueue); }
  // BIG·5c·2 — his library, so a chapter-minted card can name the chapter it came from
  const [sources, setSources] = useState([]);
  useEffect(() => { api.library.list().then((l) => setSources(l.sources)).catch(() => {}); }, []);
  useEffect(() => { refresh(); }, []);

  const pending = queue ? queue.filter((c) => c.exported !== true) : [];

  // BIG·6b — the selection, kept HONEST against the live list: a card that left the queue (deleted
  // here, or exported from the Anki view) must not stay silently ticked and inflate the count in the
  // confirm. What is offered is what is pending, so the selection is intersected with it.
  const pendingIds = new Set(pending.map((c) => c.id));
  const picked = [...selected].filter((id) => pendingIds.has(id));
  const allPicked = pending.length > 0 && picked.length === pending.length;

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setConfirmDel(false); // changing the set closes a stale confirm — it named a count that moved
  }

  function toggleAll() {
    setSelected(allPicked ? new Set() : new Set(pending.map((c) => c.id)));
    setConfirmDel(false);
  }

  async function deleteSelected() {
    if (picked.length === 0 || busy) return;
    setBusy(true); setError("");
    try {
      const r = await api.cards.remove(picked);
      if (!r.ok) { setError("Couldn't delete those — try again."); return; }
      // Report what did NOT happen rather than quietly showing a shorter list: a skipped id means
      // something moved under him (already gone, or exported from another tab), and he should know.
      const missed = (r.data && r.data.skipped ? r.data.skipped.length : 0);
      if (missed > 0) {
        setError(`${missed} card${missed === 1 ? "" : "s"} couldn't be deleted — ${missed === 1 ? "it was" : "they were"} already gone or already pushed to Anki.`);
      }
      setSelected(new Set());
      setConfirmDel(false);
      await refresh();
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally {
      setBusy(false);
    }
  }

  async function onPickPhoto(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = ""; // let him re-pick the same file after removing it
    if (!file) return;
    setBusy(true); setError("");
    try {
      const { mediaType, data } = await downscaleImage(file);
      setAttach({ kind: "image", name: file.name, mediaType, data });
    } catch {
      setError("Couldn't read this image — try a JPG or PNG.");
    } finally {
      setBusy(false);
    }
  }
  async function onPickPdf(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = "";
    if (!file) return;
    setBusy(true); setError("");
    try {
      const { data } = await readPdf(file);
      setAttach({ kind: "pdf", name: file.name, mediaType: "application/pdf", data });
    } catch {
      setError("Couldn't read that PDF — try a different file.");
    } finally {
      setBusy(false);
    }
  }
  function removeAttach() { setAttach(null); setError(""); }

  /** The source union body for the notes route — the SAME shape the card generate route takes. */
  function noteSource() {
    const guide = notes.trim() || undefined;
    if (attach?.kind === "image") return { image: { mediaType: attach.mediaType, data: attach.data }, ...(guide ? { notes: guide } : {}) };
    if (attach?.kind === "pdf") return { pdf: { mediaType: attach.mediaType, data: attach.data }, ...(guide ? { notes: guide } : {}) };
    return { notes };
  }

  /**
   * NTS·5 — fetch the notes preview. For "both" the draft cards ride along so the model maps each to
   * a section. A notes FAILURE inside "both" is non-fatal: the cards are already good and headed for
   * Anki, so he keeps them rather than losing the capture.
   */
  async function fetchNotes(draftCards) {
    const r = await api.notes.generate(noteSource(), draftCards);
    if (r.ok && r.data?.deferred) return { error: "Couldn't build notes from that — give it another try." };
    if (!r.ok || !r.data?.preview) return { error: "Couldn't build notes from that — give it another try." };
    const p = r.data.preview;
    if (!p.sections || p.sections.length === 0) return { error: "No notes came back — try a longer or clearer source." };
    return {
      note: { title: p.title || "", sections: p.sections.map((s) => ({ ...s })) },
      cardSections: Array.isArray(r.data.cardSections) ? r.data.cardSections : null,
    };
  }

  async function generate() {
    if (!canGenerate) return;
    setBusy(true); setError("");
    try {
      // Notes-only never asks for cards at all — one call, no card generation.
      if (product === "notes") {
        const n = await fetchNotes();
        if (n.error) { setError(n.error); return; }
        setPreview([]); setNoteDraft(n.note); setPhase("preview");
        return;
      }
      const guide = notes.trim() || undefined;
      const r = attach?.kind === "image"
        ? await api.cards.generateImage({ mediaType: attach.mediaType, data: attach.data }, guide)
        : attach?.kind === "pdf"
          ? await api.cards.generatePdf({ mediaType: attach.mediaType, data: attach.data }, guide)
          : await api.cards.generate(notes);
      const code = r.data?.code;
      if (r.ok && r.data && Array.isArray(r.data.cards)) {
        const previewCards = r.data.cards.map((c, i) => ({ _k: i, front: c.front, back: c.back, tags: c.tags || [], selected: true, dup: null, section: null }));
        setPreview(previewCards);
        setNoteDraft(null);
        setPhase("preview");
        // NTS·5 "both": now ask for the notes, handing the drafts over for the section mapping.
        if (product === "both") {
          const n = await fetchNotes(previewCards.map(({ front, back }) => ({ front, back })));
          if (n.error) {
            setError(`${n.error} Your cards are ready below.`); // the cards survive a notes failure
          } else {
            setNoteDraft(n.note);
            if (n.cardSections) setPreview((prev) => prev.map((c, i) => ({ ...c, section: n.cardSections[i] ?? null })));
          }
        }
        // PC·2: flag near-duplicates against the queue; flagged cards are DESELECTED (one tap re-adds)
        api.cards.checkDuplicates(previewCards.map(({ front, back }) => ({ front, back }))).then((dr) => {
          const flags = dr.ok && dr.data && Array.isArray(dr.data.flags) ? dr.data.flags : [];
          setPreview((prev) => prev.map((c, i) => flags[i]?.match ? { ...c, dup: flags[i].match, selected: false } : c));
        }).catch(() => {});
      } else if (r.status === 422 || code === "unparseable") {
        setError(attach ? "Couldn't read that — try a clearer photo or a different PDF." : "Couldn't read that — shorten your notes and try again.");
      } else if (r.status === 415 || code === "unsupported") {
        setError(attach?.kind === "pdf" ? "That file isn't a readable PDF — export or save it as a PDF." : "That image type isn't supported — use a JPG, PNG, or WebP.");
      } else if (code === "too_big") {
        setError(attach?.kind === "pdf" ? "That PDF is too large — try fewer pages or split it." : "That photo is too large — try a smaller photo.");
      } else if (code === "too_long") {
        setError(`Those notes are a bit long — trim them under ${MAX_NOTES} characters and retry.`);
      } else if (r.status === 413) {
        // X·1: a 413 with no JSON code is an upload-size rejection UPSTREAM of the app (proxy /
        // body limit) — it must never masquerade as notes-too-long or PDF-size copy.
        setError("That file is too large to upload — try a smaller one.");
      } else {
        setError("Something went wrong generating cards. Give it another try.");
      }
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally {
      setBusy(false); // always clears the spinner, success or failure
    }
  }

  function editCard(k, field, value) {
    setPreview((prev) => prev.map((c) => (c._k === k ? { ...c, [field]: value } : c)));
  }
  function dropCard(k) { setPreview((prev) => prev.filter((c) => c._k !== k)); }

  const keepable = preview.filter((c) => c.selected && c.front.trim() && c.back.trim());
  function toggleCard(k) { setPreview((prev) => prev.map((c) => (c._k === k ? { ...c, selected: !c.selected } : c))); }

  // NTS·5 — edit a note section in place (heading + content only; key terms are the model's).
  function editSection(i, field, value) {
    setNoteDraft((prev) => prev && ({ ...prev, sections: prev.sections.map((s, j) => (j === i ? { ...s, [field]: value } : s)) }));
  }
  // A section the server's cleanNotes would DROP (blank heading or content) — he must fix it, or the
  // saved note silently loses that section and takes its cards' provenance with it.
  const badSections = noteDraft ? noteDraft.sections.filter((s) => !s.heading.trim() || !s.content.trim()).length : 0;
  const noteSaveable = !noteDraft || (Boolean(noteDraft.title.trim()) && noteDraft.sections.length > 0 && badSections === 0);
  const canSave = !busy && noteSaveable && (noteDraft ? true : keepable.length > 0);

  async function confirm() {
    if (!canSave || busy) return;
    setBusy(true); setError("");
    try {
      // SAVE ORDER (the client's job, per NTS·2): the note first, so its id exists before any card
      // references it. If the note fails, NOTHING is saved — he keeps the preview and retries.
      let noteRefFor = () => undefined;
      if (noteDraft) {
        const nr = await api.notes.save({
          title: noteDraft.title.trim(),
          sections: noteDraft.sections.map(({ heading, content, keyTerms }) => ({ heading, content, ...(keyTerms ? { keyTerms } : {}) })),
          sourceKind: attach?.kind === "image" ? "image" : attach?.kind === "pdf" ? "pdf" : "text",
        });
        if (!nr.ok || !nr.data?.id) { setError("Couldn't save those notes — give it another try."); return; }
        const saved = nr.data;
        // POSITIONAL identity (RVW·1): the confirmed preview's section ORDER is the saved order —
        // Save is blocked while any section is blank, so the server's cleanNotes drops nothing.
        // This used to match by HEADING, which was wrong: two sections sharing a heading made the
        // lookup last-wins and pinned a card to the WRONG section. If the counts disagree at all,
        // every ref degrades to NOTHING — provenance is a convenience, and a guess is a lie.
        const aligned = Array.isArray(saved.sections) && saved.sections.length === noteDraft.sections.length;
        noteRefFor = (c) => {
          if (!aligned || !Number.isInteger(c.section)) return undefined;
          return c.section >= 0 && c.section < saved.sections.length ? { noteId: saved.id, section: c.section } : undefined;
        };
      }
      if (keepable.length) {
        // The noteRef rides the SAVE only — it is in-app metadata. His Anki export never reads it.
        const r = await api.cards.save(keepable.map((c) => {
          const ref = noteRefFor(c);
          return { front: c.front, back: c.back, tags: c.tags, ...(ref ? { noteRef: ref } : {}) };
        }));
        if (!r.ok) { setError("Couldn't save those cards — give it another try."); return; }
      }
      setNotes(""); setAttach(null); setPreview([]); setNoteDraft(null); setPhase("compose"); refresh();
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally {
      setBusy(false);
    }
  }

  // The pending-export queue — always visible under the capture flow: what Anki hasn't
  // confirmed yet. The push itself lives in the Anki view (DY·4).
  const pendingList = (
    <section className="pending">
      <div className="study-head">
        <h2>Pending export{queue !== null ? ` · ${pending.length}` : ""}</h2>
        {/* BIG·6b — DELETE IS THE REVIEW STEP for a chapter run. Select-all is here because 150
            cards is the case this exists for; ticking them one at a time would not be an answer. */}
        {pending.length > 0 && (
          <div className="pending-actions">
            <button type="button" className="link" onClick={toggleAll}>
              {allPicked ? "Clear selection" : `Select all ${pending.length}`}
            </button>
            {picked.length > 0 && (
              <button type="button" className="link" onClick={() => setConfirmDel(true)} disabled={busy}>
                Delete {picked.length}
              </button>
            )}
          </div>
        )}
      </div>
      {/* The confirm NAMES THE COUNT, because deleting the wrong one out of 150 is easy and there is
          no undo — and it says what SURVIVES, so "delete" never reads as "delete the chapter too". */}
      {confirmDel && picked.length > 0 && (
        <p className="del-warn" role="alert">
          Delete {picked.length} card{picked.length === 1 ? "" : "s"}? This can&apos;t be undone.
          The chapter they came from stays in your library, and cards you&apos;ve already pushed to
          Anki aren&apos;t touched.{" "}
          <button type="button" className="link" onClick={deleteSelected} disabled={busy}>
            {busy ? "Deleting…" : `Delete ${picked.length}`}
          </button>{" "}
          <button type="button" className="link" onClick={() => setConfirmDel(false)}>Keep them</button>
        </p>
      )}
      {queue === null
        ? <p className="muted">Loading…</p>
        : pending.length === 0
          ? <p className="muted empty">Nothing waiting — saved cards queue here until Anki confirms them.</p>
          : <ul className="deck-list">
              {pending.map((c) => (
                <li key={c.id} className="deck-row">
                  <label className="pending-pick">
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                    <span className="sr-only">Select this card</span>
                  </label>
                  <div className="deck-meta">
                    {/* IMG·4 — the image he approved, shown where he will recognise the card. It
                        rides the Anki push as a media file; here it is just a thumbnail. */}
                    <CardImage image={c.image} />
                    <span className="deck-name" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(c.front) }} />
                    {Array.isArray(c.tags) && c.tags.length > 0 &&
                      <span className="deck-counts">{c.tags.join(" · ")}</span>}
                    <SourceLine card={c} sources={sources} />
                    <div className="image-affordance">
                      {/* SF·1 — only a chapter card can offer its source figure (absent means absent) */}
                      {c.sourceRef && (
                        <button type="button" className="link figure-add"
                          onClick={() => { setPickingId(null); setCroppingId((id) => (id === c.id ? null : c.id)); }}>
                          {c.image ? "Re-crop figure" : "Add figure"}
                        </button>
                      )}
                      <button type="button" className="link" onClick={() => { setCroppingId(null); setPickingId((id) => (id === c.id ? null : c.id)); }}>
                        {c.image ? "Change image" : "Add an image"}
                      </button>
                      {c.image && (
                        <button type="button" className="link"
                          onClick={async () => { const r = await api.cards.imageDetach(c.id); if (r.ok) refresh(); }}>
                          Remove image
                        </button>
                      )}
                    </div>
                    {croppingId === c.id && c.sourceRef && (
                      <FigureCropper card={c} source={sources.find((s) => s && s.id === c.sourceRef.libraryId)}
                        onAttached={() => { setCroppingId(null); refresh(); }}
                        onClose={() => setCroppingId(null)} />
                    )}
                    {pickingId === c.id && (
                      <ImagePicker card={c}
                        onAttached={() => { setPickingId(null); refresh(); }}
                        onClose={() => setPickingId(null)} />
                    )}
                  </div>
                </li>
              ))}
            </ul>}
    </section>
  );

  if (phase === "preview") {
    return (
      <div className="study">
        <header className="study-top">
          <button className="link" onClick={() => { setPhase("compose"); setError(""); }}>← Back</button>
          <span className="review-progress">{keepable.length} to save</span>
        </header>
        <h1 className="study-head">{noteDraft && !preview.length ? "Review notes" : "Review cards"}</h1>
        {error && <p className="error" role="alert">{error}</p>}
        {noteDraft && (
          <section className="note-preview">
            <p className="notes-tagline">{NOTES_TAGLINE}</p>
            <label className="field"><span>Notes title</span>
              <input value={noteDraft.title} onChange={(e) => setNoteDraft((p) => ({ ...p, title: e.target.value }))} /></label>
            {noteDraft.sections.map((s, i) => (
              <div key={i} className="note-section-edit">
                <label className="field"><span>Section {i + 1}</span>
                  <input value={s.heading} onChange={(e) => editSection(i, "heading", e.target.value)} /></label>
                <label className="field"><span>Notes</span>
                  <textarea rows={4} value={s.content} onChange={(e) => editSection(i, "content", e.target.value)} /></label>
                {s.keyTerms?.length > 0 && <p className="note-terms">{s.keyTerms.join(" · ")}</p>}
              </div>
            ))}
            {badSections > 0 && <p className="error" role="alert">Every section needs a heading and notes — fill in or clear the blanks.</p>}
            {!noteDraft.title.trim() && <p className="error" role="alert">Give these notes a title.</p>}
          </section>
        )}
        {preview.length === 0
          ? (noteDraft ? null : <p className="muted empty">No cards came back — try more detailed notes.</p>)
          : <ul className="gen-preview">
              {preview.map((c) => (
                <li key={c._k} className={`gen-card${c.dup && !c.selected ? " gen-card-skip" : ""}`}>
                  {c.dup && (
                    <p className="dup-note" role="note">You may already have this — “{c.dup.front.length > 60 ? c.dup.front.slice(0, 59) + "…" : c.dup.front}”.{" "}
                      <button className="link" onClick={() => toggleCard(c._k)}>{c.selected ? "Skip it" : "Add anyway"}</button>
                    </p>
                  )}
                  <label className="field"><span>Front</span>
                    <textarea rows={2} value={c.front} onChange={(e) => editCard(c._k, "front", e.target.value)} /></label>
                  <label className="field"><span>Back</span>
                    <textarea rows={2} value={c.back} onChange={(e) => editCard(c._k, "back", e.target.value)} /></label>
                  <div className="gen-card-foot">
                    {c.tags.length > 0 && <span className="gen-tags">{c.tags.join(" · ")}</span>}
                    {noteDraft && c.section != null && noteDraft.sections[c.section] && (
                      // NTS·5 — the provenance tag: which note section this card came from. In-app
                      // only; it is NOT one of the fields that goes to Anki.
                      <span className="gen-section">From: {noteDraft.sections[c.section].heading}</span>
                    )}
                    <button className="link" onClick={() => dropCard(c._k)}>Drop</button>
                  </div>
                </li>
              ))}
            </ul>}
        <div className="cardform-actions">
          <button className="btn" onClick={confirm} disabled={!canSave}>
            {busy ? "Saving…"
              : noteDraft && keepable.length ? `Save notes + ${keepable.length} card${keepable.length === 1 ? "" : "s"} to the queue`
                : noteDraft ? "Save notes"
                  : `Save ${keepable.length} card${keepable.length === 1 ? "" : "s"} to the queue`}
          </button>
          <button className="link" onClick={() => { setPreview([]); setNoteDraft(null); setPhase("compose"); setError(""); }}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
      </header>
      <h1 className="study-head">Study &amp; Cards</h1>
      <div className="cardform">
        {/* NTS·5 — what this capture makes. Cards is the DEFAULT and its path is unchanged. */}
        <fieldset className="product-picker">
          <legend>Make</legend>
          {[["cards", "Cards"], ["notes", "Study notes"], ["both", "Both"]].map(([v, label]) => (
            <label key={v} className={`product-opt${product === v ? " is-on" : ""}`}>
              <input type="radio" name="product" value={v} checked={product === v}
                onChange={() => { setProduct(v); setError(""); }} />
              <span>{label}</span>
            </label>
          ))}
          <p className="product-hint">{NOTES_TAGLINE}</p>
        </fieldset>
        {/* PMT·1 — a preset fills the box below; it stays editable, and nothing submits */}
        <PresetPicker onPick={(text) => setNotes(text)} />
        <label className="field">
          <span>{attach ? "Guidance (optional)" : "Paste your notes"}</span>
          <textarea rows={attach ? 3 : 10} value={notes} autoFocus={!attach}
            placeholder={attach
              ? "Optional — tell the AI what to focus on (e.g. “occlusion, focus on definitions”)"
              : "Paste lecture notes here — a paragraph, a page, a study guide…"}
            onChange={(e) => setNotes(e.target.value)} />
        </label>
        {!attach && <p className={tooLong ? "gen-count over" : "gen-count"}>{notes.length} / {MAX_NOTES} characters</p>}
        <div className="gen-source">
          {attach
            ? <span className="gen-chip">{attach.kind === "pdf" ? "📄" : "📷"} {attach.name} · {fmtBytes(b64Bytes(attach.data))}
                <button className="link" onClick={removeAttach}>Remove</button></span>
            : <>
                <label className="gen-photo-add"><span className="link">Add a photo</span>
                  <input type="file" accept="image/*" onChange={onPickPhoto} style={{ display: "none" }} /></label>
                <label className="gen-photo-add"><span className="link">Add a PDF</span>
                  <input type="file" accept="application/pdf" onChange={onPickPdf} style={{ display: "none" }} /></label>
                <p className="gen-chapter-hint">Whole chapter? Add it to your <strong>Chapter Library</strong> instead — a capture reads about one page&apos;s worth.</p>
              </>}
        </div>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="cardform-actions">
          <button className="btn" onClick={generate} disabled={!canGenerate}>
            {busy ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>
      {pendingList}
    </div>
  );
}

// --- DY·4: the Anki view — when AnkiConnect answers: today's review stat + push the pending
// queue; when it doesn't: the honest setup state (this only works on his laptop, next to Anki
// Desktop — phones review via AnkiWeb after Anki syncs). Only ANKI-CONFIRMED adds flip the
// exported flag; skipped notes (usually duplicates) stay pending and say so.
// NTS·5 — the Study Notes surface, IN-APP. His cards leave for Anki; the notes stay here, which is
// exactly the point: this is the one study-adjacent surface he reads inside the app. It is NOT study
// data — it carries no schedule, no learning state, and imports nothing from core/srs (the seam's
// static grep-proof covers this file automatically). Content renders through the SAME escape-first
// markdown subset his pending-export list already uses (C·5).
/** The statuses a chapter job is still moving through — the tracker polls only while one holds. */
const JOB_LIVE = new Set(["queued", "planning", "running", "stalled"]);

// ── BIG·5b·2 — THE CHAPTER SCREEN: start, consent, and the tracker ────────────────────────────
// This is the slice the whole BIG chain was built for. A chapter is many model passes over many
// minutes, and the ONE failure that matters is a kid staring at a screen unable to tell working
// from broken. So: nothing long starts without a warning, progress is always visible and always
// named, and every ending says which ending it was.

/** How often the tracker asks. Fast enough to feel alive, slow enough to be nothing. */
const CHAPTER_POLL_MS = 1500;

/**
 * A rough, HONESTLY-HEDGED runtime for the consent gate. One quality pass per section plus the
 * coverage pass; real passes land well under their 120s ceiling, so this is a range and is labelled
 * as one. A wrong-but-stated estimate is what makes a long wait bearable; no estimate at all is
 * what makes it feel broken.
 */
function estimateMinutes(sections) {
  const n = Math.max(1, Number(sections) || 1);
  return { low: Math.max(1, Math.round((n * 20) / 60)), high: Math.max(2, Math.round((n * 55) / 60)) };
}

// BIG·5c·2 — PROVENANCE, said where the kid can act on it. A chapter-minted card carries
// `sourceRef { libraryId, pageStart, pageEnd }` (BIG·4: shape-only, stamped per segment by the
// runner); until now it was stored and synced and never shown. A kid staring at a card they doubt
// can now read "from ch4.pdf, pages 30–34" and go check the page. The filename resolves through
// the library listing the client already fetches; a source deleted to free quota (sourceRef
// deliberately survives that) reads "a deleted chapter" — the raw `lib_` id is never rendered.
// A card with no sourceRef — every hand-written card, every card older than BIG·4, i.e. most of
// them — renders NOTHING: no node, no placeholder. Text nodes only: a filename is stored data and
// must render inert even if it carries markup.
function SourceLine({ card, sources }) {
  const ref = card && card.sourceRef;
  if (!ref || typeof ref !== "object" || typeof ref.libraryId !== "string" || !ref.libraryId) return null;
  if (!Number.isInteger(ref.pageStart) || ref.pageStart < 1 || !Number.isInteger(ref.pageEnd) || ref.pageEnd < ref.pageStart) return null;
  const src = Array.isArray(sources) ? sources.find((s) => s && s.id === ref.libraryId) : null;
  const name = src && typeof src.filename === "string" && src.filename ? src.filename : "a deleted chapter";
  return <span className="card-source">from {name}, {pageRange(ref.pageStart, ref.pageEnd)}</span>;
}

/** "pages 12–14" / "page 12" — the same shape everywhere a range is shown. */
function pageRange(a, b) {
  if (!a) return "";
  return a === b ? `page ${a}` : `pages ${a}–${b}`;
}

/** The one-line answer to "what is it doing right now", from the record alone. */
function liveLine(job) {
  const segs = job.segments || [];
  const done = segs.filter((s) => s.status === "done" || s.status === "failed").length;
  const running = segs.find((s) => s.status === "running");
  const at = running ? segs.indexOf(running) + 1 : Math.min(done + 1, segs.length);
  return { at, of: segs.length, label: (running || segs[at - 1] || {}).label || "", done };
}

/** CST·1 — "Tokens spent: 12000 · 400 per card", honest "—" for anything not actually measured.
 *  CST·2 — with cache counters recorded: "Tokens spent: 120000 (91000 cached) · 400 per card". The
 *  API's input_tokens EXCLUDES what the cache served or wrote, so the total is input + output +
 *  cache read + cache creation; without cache counters that is exactly the CST·1 sum, and the
 *  line renders byte-for-byte as before. "cached" is what was READ from cache — the saving. */
function tokensOf(job) {
  const u = job && job.usage;
  if (!u || !Number.isFinite(u.inputTokens) || !Number.isFinite(u.outputTokens)) return { total: null, cached: null, perCard: null };
  const read = Number.isFinite(u.cacheReadInputTokens) ? u.cacheReadInputTokens : null;
  const wrote = Number.isFinite(u.cacheCreationInputTokens) ? u.cacheCreationInputTokens : 0;
  const total = u.inputTokens + u.outputTokens + (read ?? 0) + wrote;
  const landed = job && job.report && job.report.landed ? job.report.landed.committed : null;
  const perCard = Number.isInteger(landed) && landed > 0 ? Math.round(total / landed) : null;
  return { total, cached: read, perCard };
}
function TokensLine({ job }) {
  const { total, cached, perCard } = tokensOf(job);
  return (
    <p className="chap-tokens muted">
      Tokens spent: {total == null ? "—" : total}{cached != null ? ` (${cached} cached)` : ""} · {perCard == null ? "—" : perCard} per card
    </p>
  );
}

// PMT·1 — THE PRESET PICKER, on both guidance surfaces. Picking a preset COPIES its body into the
// box through `onPick` — the box stays editable per run and NOTHING submits. "Manage" opens a
// minimal create / rename / edit / delete form in place. Presets ride sync, so his laptop and
// phone see one set.
function PresetPicker({ onPick }) {
  const [presets, setPresets] = useState(null);
  const [managing, setManaging] = useState(false);
  const [editId, setEditId] = useState("");   // "" = a new preset
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [note, setNote] = useState("");
  function refresh() { return api.presets.list().then(setPresets); }
  useEffect(() => { refresh(); }, []);

  function pick(id) {
    const p = (presets || []).find((x) => x.id === id);
    if (p) onPick(p.body);
  }
  function startEdit(id) {
    const p = (presets || []).find((x) => x.id === id);
    setEditId(p ? p.id : ""); setTitle(p ? p.title : ""); setBody(p ? p.body : ""); setNote("");
  }
  async function save() {
    if (!title.trim() || !body.trim()) { setNote("A preset needs a title and a body."); return; }
    const r = editId ? await api.presets.update(editId, { title, body }) : await api.presets.create(title, body);
    if (!r.ok) { setNote((r.data && r.data.error) || "That couldn't be saved."); return; }
    setNote(editId ? "Saved." : "Added."); setEditId(""); setTitle(""); setBody("");
    await refresh();
  }
  async function remove() {
    if (!editId) return;
    const r = await api.presets.remove(editId);
    if (!r.ok) { setNote("That couldn't be deleted."); return; }
    setEditId(""); setTitle(""); setBody(""); setNote("Deleted.");
    await refresh();
  }

  if (presets === null) return null;
  return (
    <div className="preset-picker">
      <label className="preset-row">
        <span className="muted">Fill from a preset (optional)</span>
        <select value="" onChange={(e) => pick(e.target.value)} disabled={presets.length === 0}>
          <option value="">{presets.length === 0 ? "No presets yet" : "Choose a preset…"}</option>
          {presets.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <button type="button" className="link" onClick={() => { setManaging((m) => !m); startEdit(""); }}>
          {managing ? "Done" : "Manage"}
        </button>
      </label>
      {managing && (
        <div className="preset-manage">
          <select value={editId} onChange={(e) => startEdit(e.target.value)} aria-label="Preset to edit">
            <option value="">+ new preset</option>
            {presets.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <input type="text" placeholder="Title" maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Preset title" />
          <textarea rows={3} placeholder="The guidance this preset fills in" maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} aria-label="Preset body" />
          <div className="preset-actions">
            <button type="button" className="btn btn-sm" onClick={save}>{editId ? "Save changes" : "Add preset"}</button>
            {editId && <button type="button" className="link" onClick={remove}>Delete</button>}
            {note && <span className="muted" role="status">{note}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function ChapterJob({ source, job, onGo, onAbort, onClose, busy }) {
  const segs = job.segments || [];
  const plan = job.plan || {};
  const report = job.report || null;
  const unreadable = (plan.unreadablePages || []).concat();
  const skipped = (report && report.skippedPages) || [];
  const cards = (job.cards || []).length;
  // BIG·6a — cards the queue refused. `landed` is null on a job that ran before the commit seam
  // existed (or at a mount without one), so read it defensively and treat absent as none lost.
  const lost = ((report && report.landed) || {}).lost || 0;
  const [confirmStop, setConfirmStop] = useState(false);

  // ── the consent gate: what the survey found, before anything long is spent ───────────────────
  if (job.awaitingConsent) {
    const est = estimateMinutes(plan.sections || segs.length);
    return (
      <section className="chap-panel">
        <h2 className="chap-head">Ready to make cards from {source ? source.filename : "this chapter"}</h2>
        <p className="chap-found">
          Found <strong>{plan.sections || segs.length} sections</strong>
          {plan.pages ? <> across {pageRange(plan.pages.start, plan.pages.end)}</> : null}.
          {" "}This usually takes about <strong>{est.low}–{est.high} minutes</strong>.
        </p>
        {unreadable.length > 0 && (
          <p className="chap-warn" role="status">
            {unreadable.length === 1 ? "One stretch" : `${unreadable.length} stretches`} couldn&apos;t be read
            {" "}({unreadable.map((u) => pageRange(u.pageStart, u.pageEnd)).join(", ")}) — those pages look scanned,
            with no text behind the picture. Everything else will still be used.
          </p>
        )}
        <ul className="chap-sections">
          {segs.map((s) => (
            <li key={s.id} className="chap-section">
              <span className="chap-section-label">{s.label}</span>
              {s.pageStart ? <span className="chap-section-pages">{pageRange(s.pageStart, s.pageEnd)}</span> : null}
            </li>
          ))}
        </ul>
        <div className="cardform-actions">
          <button className="btn" onClick={onGo} disabled={busy}>{busy ? "Starting…" : "Start making cards"}</button>
          <button className="link" onClick={onAbort}>Not now</button>
        </div>
      </section>
    );
  }

  // ── the terminal states, each said in its own words ──────────────────────────────────────────
  if (job.status === "done" || job.status === "failed" || job.status === "aborted") {
    const ceiling = job.error && (job.error.code === "max_passes" || job.error.code === "max_wall_clock");
    return (
      <section className="chap-panel">
        <h2 className="chap-head">
          {job.status === "done"
            ? `${cards} card${cards === 1 ? "" : "s"} ready`
            : ceiling
              ? "Stopped early"
              : job.status === "aborted" ? "Stopped" : "Couldn't finish this chapter"}
        </h2>
        {job.status === "done" && (
          <p className="chap-note">They&apos;re in your pending export queue — push them to Anki when you&apos;re ready.</p>
        )}
        {/* CST·1 — what the factory spent, in tokens (never dollars here). `usage` is absent on a job
            nothing reported for, and per-card divides by what LANDED, so both read "—" rather than a
            number that would mean nothing — the BIG·5c rule. */}
        <TokensLine job={job} />
        {lost > 0 && (
          // BIG·6a — the one case the count above could lie about: the queue refused some of them.
          // Silence here would show him a number he cannot find, which is the bug this lesson fixes.
          <p className="chap-warn" role="alert">
            {lost} of them couldn&apos;t be saved to your queue. The rest are there — run the chapter
            again to pick up the ones that didn&apos;t make it.
          </p>
        )}
        {ceiling && (
          <p className="chap-warn" role="status">
            This chapter ran longer than one job is allowed to. <strong>The {cards} card{cards === 1 ? "" : "s"} it
            already made are in your queue</strong> — start it again to pick up the rest.
          </p>
        )}
        {job.status === "aborted" && !ceiling && (
          <p className="chap-note">The {cards} card{cards === 1 ? "" : "s"} it made before you stopped it are in your queue.</p>
        )}
        {job.status === "failed" && job.error && <p className="error" role="alert">{job.error.message}</p>}
        <ChapterGaps skipped={skipped} unreadable={unreadable} />
        <div className="cardform-actions">
          <button className="btn" onClick={onClose}>Done</button>
        </div>
      </section>
    );
  }

  // ── stalled: BIG·1 derives this at read. Never dress it as progress. ─────────────────────────
  if (job.status === "stalled") {
    return (
      <section className="chap-panel">
        <h2 className="chap-head">This looks stuck</h2>
        <p className="chap-warn" role="alert">
          Nothing has finished for a while. It may still be working, but it may not be.
          {cards > 0 ? ` The ${cards} card${cards === 1 ? "" : "s"} it already made are safe either way.` : ""}
        </p>
        <StopButton confirmStop={confirmStop} setConfirmStop={setConfirmStop} onAbort={onAbort} cards={cards} />
      </section>
    );
  }

  // ── working ──────────────────────────────────────────────────────────────────────────────────
  const live = liveLine(job);
  const pct = live.of ? Math.round((live.done / live.of) * 100) : 0;
  return (
    <section className="chap-panel">
      <h2 className="chap-head">Reading {source ? source.filename : "your chapter"}…</h2>
      <p className="chap-progress" role="status">
        {job.status === "queued" || job.status === "planning"
          ? "Looking through the chapter to see what's in it…"
          : <>Section <strong>{live.at}</strong> of <strong>{live.of}</strong>
              {live.label ? <> — {live.label}</> : null}</>}
      </p>
      <div className="meter-track" role="img" aria-label={`${live.done} of ${live.of} sections finished`}>
        <span className="meter-fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="chap-count">{cards} card{cards === 1 ? "" : "s"} so far</p>
      <ul className="chap-sections">
        {segs.map((s) => (
          <li key={s.id} className={`chap-section chap-${s.status}`}>
            <span className="chap-dot" aria-hidden="true">
              {s.status === "done" ? "✓" : s.status === "failed" ? "!" : s.status === "running" ? "•" : "·"}
            </span>
            <span className="chap-section-label">{s.label}</span>
            {s.pageStart ? <span className="chap-section-pages">{pageRange(s.pageStart, s.pageEnd)}</span> : null}
            <span className="chap-section-state">
              {s.status === "done" ? `${s.cardCount || 0} cards` : s.status === "failed" ? "skipped" : s.status === "running" ? "reading…" : "waiting"}
            </span>
          </li>
        ))}
      </ul>
      <StopButton confirmStop={confirmStop} setConfirmStop={setConfirmStop} onAbort={onAbort} cards={cards} />
    </section>
  );
}

/** Stopping must never feel like throwing the work away — because BIG·1 guarantees it isn't. */
function StopButton({ confirmStop, setConfirmStop, onAbort, cards }) {
  if (!confirmStop) return <button className="link chap-stop" onClick={() => setConfirmStop(true)}>Stop</button>;
  return (
    <span className="del-warn" role="alert">
      Stop making cards from this chapter?{" "}
      {cards > 0
        ? `The ${cards} card${cards === 1 ? "" : "s"} it already made are kept.`
        : "Nothing is lost — you can start it again."}{" "}
      <button className="link" onClick={onAbort}>Stop it</button>{" "}
      <button className="link" onClick={() => setConfirmStop(false)}>Keep going</button>
    </span>
  );
}

/** What a kid must be told or they will study a hole: pages that produced nothing, and why. */
function ChapterGaps({ skipped, unreadable }) {
  if (!skipped.length && !unreadable.length) return null;
  return (
    <div className="chap-gaps">
      {skipped.length > 0 && (
        <>
          <p className="chap-gaps-head">These pages didn&apos;t make any cards:</p>
          <ul className="chap-gap-list">
            {skipped.map((g, i) => (
              <li key={i}>
                <strong>{pageRange(g.pageStart, g.pageEnd) || "some pages"}</strong>
                {g.label ? ` (${g.label})` : ""} — {g.reason}
              </li>
            ))}
          </ul>
          <p className="chap-gaps-note">Worth a read on your own — nothing here is covered by a card.</p>
        </>
      )}
      {unreadable.length > 0 && (
        <p className="chap-gaps-note">
          {unreadable.map((u) => pageRange(u.pageStart, u.pageEnd)).join(", ")} couldn&apos;t be read at all —
          they look scanned, with no text behind the picture.
        </p>
      )}
    </div>
  );
}

/**
 * BIG·5b·2 — the live chapter job, as a view of SERVER state.
 *
 * RESUMABILITY IS THE POINT. The job lives server-side; this hook holds no truth of its own. On
 * mount it asks "what is running?" with no id at all (BIG·5b·2's id-less status read), so a closed
 * tab, a refresh, or a second device reattaches to the work in flight instead of showing nothing —
 * or, worse, tempting a second start.
 */
function useChapterJob() {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  // adopt whatever is already running, before anything else happens
  useEffect(() => {
    let alive = true;
    api.jobs.active().then((j) => { if (alive) setJob(j); });
    return () => { alive = false; };
  }, []);

  // poll while it is live — and stop the moment it is not. A terminal record never changes again,
  // so continuing to ask would be pure noise.
  useEffect(() => {
    if (!job || !JOB_LIVE.has(job.status)) return undefined;
    let alive = true;
    const t = setInterval(async () => {
      const next = await api.jobs.status(job.id);
      if (alive && next) setJob(next);
    }, CHAPTER_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [job && job.id, job && job.status]);

  async function start(libraryId, guidance) {
    setBusy(true);
    try {
      const r = await api.jobs.startChapter(libraryId, guidance);
      // 200 = BIG·5a's serialization answering: a job is already in flight and THIS is it. Adopting
      // it is the correct outcome, not an error — the kid sees the same tracker, never a second one.
      if (r.ok && r.data && r.data.job) {
        setJob(r.data.job);
        return { ok: true, adopted: r.data.created === false, guidanceTruncated: r.data.guidanceTruncated === true };
      }
      return { ok: false, error: (r.data && r.data.error) || "That chapter couldn't be started. Try again." };
    } finally { setBusy(false); }
  }

  async function go() {
    if (!job) return;
    setBusy(true);
    try {
      const r = await api.jobs.runChapter(job.id);
      if (r.ok && r.data && r.data.job) setJob(r.data.job);
      else setJob(await api.jobs.status(job.id));
    } finally { setBusy(false); }
  }

  async function stop() {
    if (!job) return;
    const r = await api.jobs.abort(job.id);
    setJob((r.ok && r.data && r.data.job) || (await api.jobs.status(job.id)));
  }

  return { job, busy, start, go, stop, clear: () => setJob(null) };
}

// ── BIG·5b·1 — THE CHAPTER LIBRARY ────────────────────────────────────────────────────────────
// The first slice of this chain a kid can actually see. It manages SOURCES: upload a chapter, see
// what is stored, delete what is finished with. There is deliberately NO "make cards" button here —
// that lands in BIG·5b·2 alongside the tracker, because a start button with no progress surface is
// exactly the "is it hung?" problem this whole chain exists to remove.
//
// THE COPY DOES ONE JOB ABOVE ALL: make it obvious which path a PDF is on. A capture is one request
// over one page's worth of material and yields a handful of cards; a chapter is stored and read
// section by section and yields many. A kid who cannot tell those apart will one day wonder why one
// PDF made twenty cards and another made two hundred, and conclude the app is broken.
const LIBRARY_BLURB =
  "Whole chapters live here — a textbook chapter, a packet, a lecture PDF. Capture is for one page or one set of notes at a time; a chapter gets read section by section, so it makes many more cards.";

/**
 * The sentence a kid actually reads when an upload is refused. The SERVER owns the wording for every
 * case it can answer — oversize names the limit, a full library names what to delete — so this
 * renders `data.error` verbatim rather than inventing a second, different answer to one question.
 * The only copy written here is for the cases the server never got to answer at all: a body the
 * proxy killed before Node saw it (a 413 with no JSON), and no network.
 */
function uploadRefusal(r) {
  if (r && r.data && typeof r.data.error === "string" && r.data.error) return r.data.error;
  if (r && r.status === 413) {
    return "That file was too big to send — it was stopped before it finished uploading. Split the chapter, or export fewer pages.";
  }
  return "That upload didn't go through. Check your connection and try again.";
}

function LibraryView({ onBack }) {
  const [lib, setLib] = useState(null);          // { sources, usage } | null while loading
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");        // an upload refusal
  const [saved, setSaved] = useState("");        // the "it landed" line
  const [confirmDel, setConfirmDel] = useState(null);
  const [blocked, setBlocked] = useState(null);  // the 409 source_busy sentence, rendered in place
  // BIG·5b·2 — the live chapter job. It is adopted from the SERVER on mount, so a refresh reattaches
  // to work in flight rather than showing nothing (or tempting a second start).
  const chapter = useChapterJob();

  function refresh() { return api.library.list().then(setLib); }
  useEffect(() => { refresh(); }, []);
  // when a run ends, the library's own numbers may have moved (a re-upload, a delete elsewhere)
  const jobStatus = chapter.job && chapter.job.status;
  useEffect(() => { if (jobStatus && !JOB_LIVE.has(jobStatus)) refresh(); }, [jobStatus]);

  // GDE·1 — his focus guidance for the next chapter. Optional; sent only when non-empty; the
  // server caps it and says so, and the copy here repeats that rather than hiding it.
  const [guide, setGuide] = useState("");

  async function makeCards(id) {
    setError(""); setSaved(""); setBlocked(null);
    const r = await chapter.start(id, guide.trim());
    if (!r.ok) setError(r.error);
    else if (r.guidanceTruncated) setSaved("Your guidance ran past 2000 characters — the first 2000 were kept.");
  }

  async function onPick(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = ""; // let the same file be re-picked after a refusal
    if (!file) return;
    setBusy(true); setError(""); setSaved(""); setBlocked(null);
    try {
      const { data } = await readPdf(file);
      const r = await api.library.upload({ mediaType: "application/pdf", data }, file.name);
      if (!r.ok) { setError(uploadRefusal(r)); return; }
      const source = r.data && r.data.source;
      setSaved(r.data && r.data.created === false
        ? `${source ? source.filename : file.name} is already in your library.`
        : `${source ? source.filename : file.name} is in your library.`);
      await refresh();
    } catch {
      setError("Couldn't read that PDF — try a different file.");
    } finally {
      setBusy(false);
    }
  }

  async function del(id) {
    setError(""); setSaved(""); setBlocked(null);
    const r = await api.library.remove(id);
    setConfirmDel(null);
    if (!r.ok) {
      // 409 source_busy is NOT a generic failure: the server wrote an actionable sentence naming the
      // file and what to do about it, and that sentence is the whole point of the refusal.
      setBlocked(r.data && r.data.error ? r.data.error : "That chapter couldn't be deleted. Try again in a moment.");
      return;
    }
    refresh();
  }

  const usage = lib && lib.usage;
  const pct = usage && usage.quotaBytes > 0
    ? Math.min(100, Math.round((usage.usedBytes / usage.quotaBytes) * 100))
    : 0;

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Home</button>
      </header>
      <h1 className="study-head">Chapter Library</h1>
      <p className="lib-blurb">{LIBRARY_BLURB}</p>

      <div className="lib-add">
        <label className="btn lib-pick">
          {busy ? "Uploading…" : "Add a chapter"}
          <input type="file" accept="application/pdf" aria-label="Add a chapter PDF"
            disabled={busy} onChange={onPick} style={{ display: "none" }} />
        </label>
        <span className="muted">PDF · up to 40 MB</span>
      </div>
      {/* GDE·1 — one optional box, read at "Make cards". It is the student's words, quoted to the
          model as DATA on every pass; it never changes the rules. */}
      <PresetPicker onPick={(text) => setGuide(text)} />
      <label className="chap-guidance">
        <span className="muted">Focus guidance for the next chapter (optional)</span>
        <textarea rows={3} value={guide} maxLength={4000} onChange={(e) => setGuide(e.target.value)}
          placeholder="e.g. Focus on the nerve pathways and foramina; skip the history section." />
      </label>
      {error && <p className="error" role="alert">{error}</p>}
      {saved && <p className="lib-saved" role="status">{saved}</p>}

      {usage && (
        <section className="lib-usage">
          <p className="lib-usage-line">
            <strong>{fmtBytes(usage.usedBytes)}</strong> of {fmtBytes(usage.quotaBytes)} used
            {" · "}{usage.count} chapter{usage.count === 1 ? "" : "s"}
          </p>
          <div className="meter-track" role="img"
            aria-label={`${fmtBytes(usage.usedBytes)} of ${fmtBytes(usage.quotaBytes)} used`}>
            <span className="meter-fill" style={{ width: `${pct}%` }} />
          </div>
        </section>
      )}

      {blocked && <p className="lib-busy" role="alert">{blocked}</p>}

      {chapter.job && (
        <ChapterJob
          job={chapter.job}
          busy={chapter.busy}
          source={lib && lib.sources.find((s) => s.id === chapter.job.libraryId)}
          onGo={chapter.go}
          onAbort={chapter.stop}
          onClose={chapter.clear}
        />
      )}

      {lib === null
        ? <p className="muted">Loading…</p>
        : lib.sources.length === 0
          ? <p className="muted empty">No chapters yet. Add one above — it stays here until you turn it into cards.</p>
          : <ul className="note-list lib-list">
              {lib.sources.map((s) => (
                <li key={s.id} className="note-row lib-row">
                  <span className="lib-name">📄 {s.filename}</span>
                  <span className="note-meta">
                    {fmtBytes(s.bytes)} · added {String(s.uploadedAt).slice(0, 10)}
                  </span>
                  {confirmDel === s.id
                    ? <span className="del-warn" role="alert">
                        Delete {s.filename}? Cards you already made from it are kept.{" "}
                        <button className="link" onClick={() => del(s.id)}>Delete</button>{" "}
                        <button className="link" onClick={() => setConfirmDel(null)}>Keep</button>
                      </span>
                    : <>
                        {/* BIG·5b·2 — one job at a time (BIG·1 serializes), so while one is live the
                            others do not offer a start that would only ever adopt it. */}
                        {!chapter.job && (
                          <button className="link lib-make" disabled={chapter.busy}
                            onClick={() => makeCards(s.id)}>Make cards</button>
                        )}
                        <button className="link" onClick={() => { setBlocked(null); setConfirmDel(s.id); }}>Delete</button>
                      </>}
                </li>
              ))}
            </ul>}
    </div>
  );
}

function NotesView({ onBack }) {
  const [notes, setNotes] = useState(null);
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(null);      // the note being read
  const [confirmDel, setConfirmDel] = useState(null);

  function refresh() { return api.notes.list().then(setNotes); }
  useEffect(() => {
    refresh();
    api.hub.courses().then((c) => setCourses(Array.isArray(c) ? c : [])).catch(() => {});
  }, []);

  const courseName = Object.fromEntries(courses.map((c) => [c.id, c.name]));

  async function del(id) {
    await api.notes.remove(id);
    setConfirmDel(null);
    if (open?.id === id) setOpen(null);
    refresh();
  }

  if (open) {
    return (
      <div className="study">
        <header className="study-top">
          <button className="link" onClick={() => setOpen(null)}>← Notes</button>
        </header>
        <h1 className="study-head">{open.title}</h1>
        <p className="notes-tagline">{NOTES_TAGLINE}</p>
        {courseName[open.courseId] && <p className="muted">{courseName[open.courseId]}</p>}
        <article className="note-read">
          {open.sections.map((s, i) => (
            <section key={i} className="note-section">
              <h2>{s.heading}</h2>
              <div className="note-content" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(s.content) }} />
              {s.keyTerms?.length > 0 && (
                <p className="note-terms">{s.keyTerms.map((t) => <span key={t} className="note-term">{t}</span>)}</p>
              )}
            </section>
          ))}
        </article>
      </div>
    );
  }

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
      </header>
      <h1 className="study-head">Study Notes</h1>
      <p className="notes-tagline">{NOTES_TAGLINE}</p>
      {notes === null
        ? <p className="muted">Loading…</p>
        : notes.length === 0
          ? <p className="muted empty">No notes yet. On a capture, choose “Study notes” or “Both”.</p>
          : <ul className="note-list">
              {notes.map((n) => (
                <li key={n.id} className="note-row">
                  <button className="link note-open" onClick={() => setOpen(n)}>{n.title}</button>
                  <span className="note-meta">
                    {courseName[n.courseId] ? `${courseName[n.courseId]} · ` : ""}
                    {n.sections.length} section{n.sections.length === 1 ? "" : "s"}
                  </span>
                  {confirmDel === n.id
                    ? <span className="del-warn" role="alert">
                        Delete these notes? This can&apos;t be undone.{" "}
                        <button className="link" onClick={() => del(n.id)}>Delete</button>{" "}
                        <button className="link" onClick={() => setConfirmDel(null)}>Keep</button>
                      </span>
                    : <button className="link" onClick={() => setConfirmDel(n.id)}>Delete</button>}
                </li>
              ))}
            </ul>}
    </div>
  );
}

function AnkiView({ onBack }) {
  const [status, setStatus] = useState(null); // null checking | { ok: true, reviewed } | { ok: false }
  const [pending, setPending] = useState(null); // the unexported queue (null while loading)
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [mediaWarning, setMediaWarning] = useState(""); // ANK·1 — pictures that did not reach Anki

  async function connect() {
    setStatus(null);
    const h = await anki.hello();
    if (!h.ok) { setStatus({ ok: false }); return; }
    const r = await anki.reviewedToday();
    setStatus({ ok: true, reviewed: r.ok ? r.count : 0 });
  }
  function refreshQueue() {
    return api.cards.list().then((c) => setPending(c.filter((x) => x.exported !== true)));
  }
  useEffect(() => { connect(); refreshQueue(); }, []);
  // BIG·5c·2 — provenance on the queue he is about to push (display only — never an Anki field)
  const [sources, setSources] = useState([]);
  useEffect(() => { api.library.list().then((l) => setSources(l.sources)).catch(() => {}); }, []);

  async function push() {
    if (!pending || pending.length === 0 || busy) return;
    setBusy(true); setError(""); setResult(""); setMediaWarning("");
    try {
      const deck = await anki.ensureDeck();
      if (!deck.ok) { setError("Anki stopped answering — is Anki Desktop still open?"); return; }
      const r = await anki.addNotes(pending);
      if (!r.ok) { setError(`Anki refused the push${r.error ? ` — ${r.error}` : "."}`); return; }
      if (r.added.length > 0) {
        const conf = await api.anki.exported(r.added);
        if (!conf.ok) { setError("Anki took the cards but the app couldn't record it — push again to reconcile."); return; }
      }
      // ANK·1 — a PICTURE THAT DID NOT MAKE IT is said out loud. The adapter has always reported
      // `mediaFailed` (cards that HAD an image it could not store) and nothing here ever read it, so
      // the note went plain, the app said "Pushed 3 to Anki." and he found out by opening Anki to a
      // card with no picture. From where he sits that is indistinguishable from the media path being
      // broken outright — which is exactly how it was reported. The note still goes: his text is
      // what he studies and a failed thumbnail must never cost him the card.
      const failed = Array.isArray(r.mediaFailed) ? r.mediaFailed.length : 0;
      setResult(`Pushed ${r.added.length} to Anki.${r.skipped.length ? ` ${r.skipped.length} skipped (already in Anki) — still pending here.` : ""}`);
      setMediaWarning(failed === 0 ? "" :
        `${failed} card${failed === 1 ? "'s" : "s'"} picture didn't make it into Anki — ${failed === 1 ? "that note" : "those notes"} arrived without it. Anki refused the media file; check that Anki Desktop can write to its collection.media folder, then push again to retry.`);
      await refreshQueue();
    } finally {
      setBusy(false);
    }
  }

  const count = pending ? pending.length : 0;

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
      </header>
      <h1 className="study-head">Anki</h1>

      {status === null
        ? <p className="muted">Looking for Anki…</p>
        : !status.ok
          ? <section className="anki-setup">
              <p className="muted">Anki isn't answering. To connect:</p>
              <ol className="setup-list">
                <li>Open <b>Anki Desktop</b> on this laptop, with the <b>AnkiConnect</b> add-on installed (add-on code <code>2055492159</code>).</li>
                <li>In AnkiConnect's config, add <code>https://dylan.school-app.net</code> to <code>webCorsOriginList</code>, then restart Anki.</li>
                <li>The push works <b>on your laptop only</b> — the browser talks to Anki at <code>localhost:8765</code>. Phones review via <b>AnkiWeb</b> after Anki syncs.</li>
              </ol>
              <button className="btn btn-sm" onClick={connect}>Try again</button>
            </section>
          : <section>
              <p className="stat"><span className="stat-num">{status.reviewed}</span><span className="stat-label">reviewed today in Anki</span></p>
              <div className="cardform-actions anki-push">
                <button className="btn" onClick={push} disabled={count === 0 || busy}>
                  {busy ? "Pushing…" : `Push ${count} pending to Anki`}
                </button>
                <span className="deck-counts">deck: {ANKI_DECK}</span>
              </div>
            </section>}

      {result && <p className="muted push-result">{result}</p>}
      {/* ANK·1 — the note went, the picture did not. Its own line, and an alert, because a silent
          plain note is the failure he actually reported. */}
      {mediaWarning && <p className="del-warn push-media-warn" role="alert">{mediaWarning}</p>}
      {error && <p className="error" role="alert">{error}</p>}

      <section className="pending">
        <div className="study-head">
          <h2>Pending export{pending !== null ? ` · ${count}` : ""}</h2>
        </div>
        {pending === null
          ? <p className="muted">Loading…</p>
          : count === 0
            ? <p className="muted empty">Queue's empty — everything saved has landed in Anki.</p>
            : <ul className="deck-list">
                {pending.map((c) => (
                  <li key={c.id} className="deck-row">
                    <div className="deck-meta">
                      <span className="deck-name" dangerouslySetInnerHTML={{ __html: renderMarkdownSubset(c.front) }} />
                      {Array.isArray(c.tags) && c.tags.length > 0 &&
                        <span className="deck-counts">{c.tags.join(" · ")}</span>}
                      <SourceLine card={c} sources={sources} />
                    </div>
                  </li>
                ))}
              </ul>}
      </section>
    </div>
  );
}

// --- DY·2: the course & assignment hub — every course, lab block, and deadline in one place.
// Copied from Estelle's view (UI JSX stays copied — settled at the Phase-3 review) WITH the X·4
// term-rename fix and restyled to Dylan's tokens. One active term shows at a time (default: the
// term of the most recently created course; a switcher appears once a second term exists).
const COURSE_COLORS = ["#005E63", "#1d4ed8", "#7c3aed", "#b45309", "#be123c", "#334155"];

function CourseDot({ color }) {
  return <span className="course-dot" style={{ background: color || "var(--muted)" }} aria-hidden="true" />;
}

function AssignmentRow({ asn, course, showCourse, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(asn.title);
  const [due, setDue] = useState(asn.due);
  const overdue = !asn.done && asn.due < localDateStr();

  async function save() {
    if (!title.trim() || !due) return;
    await api.hub.updateAssignment(asn.id, { title: title.trim(), due });
    setEditing(false); onRefresh();
  }
  async function toggleDone() { await api.hub.updateAssignment(asn.id, { done: !asn.done }); onRefresh(); }
  async function remove() { await api.hub.deleteAssignment(asn.id); onRefresh(); }

  if (editing) {
    return (
      <li className="asn-row asn-edit">
        <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due" />
        <button className="btn btn-sm" onClick={save} disabled={!title.trim() || !due}>Save</button>
        <button className="link" onClick={() => { setEditing(false); setTitle(asn.title); setDue(asn.due); }}>Cancel</button>
      </li>
    );
  }
  return (
    <li className={asn.done ? "asn-row is-done" : "asn-row"}>
      <label className="asn-check">
        <input type="checkbox" checked={!!asn.done} onChange={toggleDone} aria-label={`Done: ${asn.title}`} />
      </label>
      <span className="asn-title">{asn.title}</span>
      {showCourse && course && <span className="asn-course"><CourseDot color={course.color} />{course.name}</span>}
      <span className={overdue ? "asn-due overdue" : "asn-due"}>{overdue ? `overdue · ${asn.due}` : asn.due}</span>
      <span className="asn-actions">
        <button className="link" onClick={() => setEditing(true)}>Edit</button>
        <button className="link" onClick={remove}>Delete</button>
      </span>
    </li>
  );
}

// E·4 — a hand-managed exam under its course (title, date, type). Mirrors AssignmentRow; no "done"
// (an exam is an event, not a task). Uses the Y·2 exm_ CRUD routes. No srs anywhere — an exam is a
// hub record, exactly like an assignment.
function ExamRow({ exam, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(exam.title);
  const [date, setDate] = useState(exam.date);
  const [type, setType] = useState(exam.type ?? "");

  async function save() {
    if (!title.trim() || !date) return;
    await api.exams.update(exam.id, { title: title.trim(), date, type: type.trim() || null });
    setEditing(false); onRefresh();
  }
  async function remove() { await api.exams.remove(exam.id); onRefresh(); }

  if (editing) {
    return (
      <li className="asn-row asn-edit">
        <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Exam title" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Exam date" />
        <input value={type} placeholder="type" className="syl-type" onChange={(e) => setType(e.target.value)} aria-label="Exam type" />
        <button className="btn btn-sm" onClick={save} disabled={!title.trim() || !date}>Save</button>
        <button className="link" onClick={() => { setEditing(false); setTitle(exam.title); setDate(exam.date); setType(exam.type ?? ""); }}>Cancel</button>
      </li>
    );
  }
  return (
    <li className="asn-row exam-row">
      <span className="asn-title">📝 {exam.title}{exam.type ? <span className="exam-type"> · {exam.type}</span> : null}</span>
      <span className="asn-due">{exam.date}</span>
      <span className="asn-actions">
        <button className="link" onClick={() => setEditing(true)}>Edit</button>
        <button className="link" onClick={remove}>Delete</button>
      </span>
    </li>
  );
}

function CourseCard({ course, assignments, exams = [], onRefresh, archived = false }) { // ARC·1 — no practice entry: Dylan's cards live in Anki
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(course.name);
  const [term, setTerm] = useState(course.term);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [xTitle, setXTitle] = useState(""); // E·4 new-exam form
  const [xDate, setXDate] = useState("");
  const [xType, setXType] = useState("");
  const [confirmDel, setConfirmDel] = useState(0); // item count from the 409 delete-guard
  const [confirmArch, setConfirmArch] = useState(false); // ARC·1 — the one confirm step before archiving

  const open_ = assignments.filter((a) => !a.done).length;

  async function addAsn() {
    if (!title.trim() || !due) return;
    await api.hub.createAssignment(course.id, title.trim(), due);
    setTitle(""); setDue(""); onRefresh();
  }
  async function addExam() {
    if (!xTitle.trim() || !xDate) return;
    await api.exams.create(course.id, xTitle.trim(), xDate, xType.trim() || undefined);
    setXTitle(""); setXDate(""); setXType(""); onRefresh();
  }
  async function saveCourse() {
    if (!name.trim() || !term.trim()) return;
    await api.hub.updateCourse(course.id, { name: name.trim(), term: term.trim() });
    setEditing(false); onRefresh();
  }
  // the delete-guard: blocked (409 + counts) unless he confirms the cascade. The 409 reports BOTH
  // assignments and exams (Y·2) — a course can now hold exams too, so count both.
  async function remove() {
    const r = await api.hub.deleteCourse(course.id, false);
    if (r.status === 409) { setConfirmDel((r.data?.assignments ?? 0) + (r.data?.exams ?? 0)); return; }
    onRefresh();
  }
  async function removeCascade() { await api.hub.deleteCourse(course.id, true); onRefresh(); }
  // ARC·1 — archive hides, never deletes; unarchive brings it straight back (so no undo prompt).
  async function archive() { await api.hub.archiveCourse(course.id); setConfirmArch(false); onRefresh(); }
  async function unarchive() { await api.hub.unarchiveCourse(course.id); onRefresh(); }

  return (
    <li className="course-card">
      <div className="course-head">
        {editing
          ? <div className="newdeck">
              <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Course name" />
              <input value={term} onChange={(e) => setTerm(e.target.value)} aria-label="Term" />
              <button className="btn btn-sm" onClick={saveCourse} disabled={!name.trim() || !term.trim()}>Save</button>
              <button className="link" onClick={() => { setEditing(false); setName(course.name); setTerm(course.term); }}>Cancel</button>
            </div>
          : <>
              <button type="button" className="course-name" onClick={() => setOpen((o) => !o)}>
                <CourseDot color={course.color} />
                <span>{course.name}</span>
                {archived && <span className="archived-badge">Archived</span>}
                <span className="deck-counts">{open_ > 0 ? `${open_} open` : "all done"}</span>
              </button>
              <span className="deck-actions">
                {/* the manual GPA entry — one letter per course, updated any time */}
                <select className="grade-pick" value={course.grade ?? ""} aria-label={`Grade: ${course.name}`}
                  onChange={(e) => api.hub.updateCourse(course.id, { grade: e.target.value || null }).then(onRefresh)}>
                  <option value="">no grade</option>
                  {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                {!archived && <button className="link" onClick={() => setEditing(true)}>Edit</button>}
                {archived
                  ? <button className="link" onClick={unarchive}>Unarchive</button>
                  : <button className="link" onClick={() => setConfirmArch(true)}>Archive</button>}
                <button className="link" onClick={remove}>Delete</button>
              </span>
            </>}
      </div>
      {confirmDel > 0 && (
        <p className="del-warn" role="alert">
          This course still has {confirmDel} item{confirmDel === 1 ? "" : "s"} (assignments &amp; exams).{" "}
          <button className="link" onClick={removeCascade}>Delete course and everything in it</button>{" "}
          <button className="link" onClick={() => setConfirmDel(0)}>Keep it</button>
        </p>
      )}
      {confirmArch && (
        <p className="del-warn" role="alert">
          Archive {course.name}? It leaves your course list and your dashboard. Nothing is deleted, and Unarchive brings it back.{" "}
          <button className="link" onClick={archive}>Archive it</button>{" "}
          <button className="link" onClick={() => setConfirmArch(false)}>Keep it</button>
        </p>
      )}
      {open && (
        <div className="course-body">
          {assignments.length === 0
            ? <p className="muted">No assignments yet.</p>
            : <ul className="asn-list">
                {[...assignments].sort(byDue).map((a) => (
                  <AssignmentRow key={a.id} asn={a} onRefresh={onRefresh} />
                ))}
              </ul>}
          <div className="asn-add">
            <input value={title} placeholder="New assignment" onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addAsn(); }} aria-label="New assignment title" />
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="New assignment due" />
            <button className="btn btn-sm" onClick={addAsn} disabled={!title.trim() || !due}>Add</button>
          </div>

          <h3 className="course-subhead">Exams</h3>
          {exams.length === 0
            ? <p className="muted">No exams yet.</p>
            : <ul className="asn-list">
                {[...exams].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)).map((x) => (
                  <ExamRow key={x.id} exam={x} onRefresh={onRefresh} />
                ))}
              </ul>}
          <div className="asn-add">
            <input value={xTitle} placeholder="New exam" onChange={(e) => setXTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addExam(); }} aria-label="New exam title" />
            <input type="date" value={xDate} onChange={(e) => setXDate(e.target.value)} aria-label="New exam date" />
            <input value={xType} placeholder="type (optional)" className="syl-type" onChange={(e) => setXType(e.target.value)} aria-label="New exam type" />
            <button className="btn btn-sm" onClick={addExam} disabled={!xTitle.trim() || !xDate}>Add exam</button>
          </div>
        </div>
      )}
    </li>
  );
}

// Y·4 — the syllabus intake screen, ported from Alivia's Y·3 (her Maverick red → Dylan's UNMC red
// via the shared syl-* classes + his own tokens). Drop a syllabus PDF, read exactly what was
// extracted, correct anything, add what was missed, save the term into the hub. Reuses the capture
// seam (readPdf + its honest size/type copy). Preview-then-save is absolute; weights are OPTIONAL,
// EDITABLE, and NEVER invented — a missing weight is an empty input, never a zero. No credits field
// (the dental curriculum is fixed-cohort). The save writes course/assignments/exams — NEVER srs.
function SyllabusIntake({ onDone, onCancel }) {
  const [phase, setPhase] = useState("compose"); // "compose" | "preview"
  const [attach, setAttach] = useState(null);    // { name, mediaType, data } — the PDF
  const [guide, setGuide] = useState("");        // optional focus guidance (the reused capture pattern)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // pct/weight are STRINGS (the raw input value): "" means empty, never 0.
  const [course, setCourse] = useState({ name: "", term: "" });
  const [asns, setAsns] = useState([]);       // [{ _k, title, due, weight }]
  const [exams, setExams] = useState([]);     // [{ _k, title, date, type, weight }]
  const [weights, setWeights] = useState([]); // [{ _k, label, pct }]
  const keyRef = useRef(0);
  const nextKey = () => (keyRef.current += 1);

  async function onPickPdf(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = ""; // let him re-pick the same file after removing it
    if (!file) return;
    setBusy(true); setError("");
    try {
      const { data } = await readPdf(file); // the SAME capture upload path (jsdom stubs window.__readPdf)
      setAttach({ name: file.name, mediaType: "application/pdf", data });
    } catch {
      setError("Couldn't read that PDF — try a different file.");
    } finally { setBusy(false); }
  }
  function removeAttach() { setAttach(null); setError(""); }

  function loadPreview(p) {
    setCourse({ name: p.course?.name || "", term: p.course?.term || "" });
    setAsns((p.assignments || []).map((a) => ({ _k: nextKey(), title: a.title || "", due: a.dueDate || "", weight: a.weight != null ? String(a.weight) : "" })));
    setExams((p.exams || []).map((x) => ({ _k: nextKey(), title: x.title || "", date: x.date || "", type: x.type || "", weight: x.weight != null ? String(x.weight) : "" })));
    // Grading weights render with their value; a weight the parse did NOT provide is never zeroed.
    // If it found none at all, seed ONE empty row — an honest invitation to fill in later.
    const gw = (p.gradingWeights || []).map((w) => ({ _k: nextKey(), label: w.label || "", pct: w.pct != null ? String(w.pct) : "" }));
    setWeights(gw.length ? gw : [{ _k: nextKey(), label: "", pct: "" }]);
  }

  async function readSyllabus() {
    if (!attach || busy) return;
    setBusy(true); setError("");
    try {
      const r = await api.syllabus.parsePdf({ mediaType: attach.mediaType, data: attach.data }, guide.trim() || undefined);
      const code = r.data?.code;
      if (r.ok && r.data?.deferred) {
        // the known Fable failure mode (refusal / truncation / transient): honest + retryable. Stay
        // on compose and KEEP everything typed (the file + the guidance) so a retry costs nothing.
        setError("Couldn't read this one — try again.");
      } else if (r.ok && r.data?.preview) {
        loadPreview(r.data.preview);
        setPhase("preview");
      } else if (r.status === 415 || code === "unsupported") {
        setError("That file isn't a readable PDF — export or save it as a PDF.");
      } else if (code === "too_big" || r.status === 413) {
        setError("That PDF is too large to upload — try fewer pages or split it.");
      } else if (r.status === 401) {
        setError("Your session expired — sign in again.");
      } else {
        setError("Something went wrong reading that syllabus. Give it another try.");
      }
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally { setBusy(false); }
  }

  const editAsn = (k, f, v) => setAsns((p) => p.map((a) => (a._k === k ? { ...a, [f]: v } : a)));
  const addAsn = () => setAsns((p) => [...p, { _k: nextKey(), title: "", due: "", weight: "" }]);
  const dropAsn = (k) => setAsns((p) => p.filter((a) => a._k !== k));
  const editExam = (k, f, v) => setExams((p) => p.map((x) => (x._k === k ? { ...x, [f]: v } : x)));
  const addExam = () => setExams((p) => [...p, { _k: nextKey(), title: "", date: "", type: "", weight: "" }]);
  const dropExam = (k) => setExams((p) => p.filter((x) => x._k !== k));
  const editWeight = (k, f, v) => setWeights((p) => p.map((w) => (w._k === k ? { ...w, [f]: v } : w)));
  const addWeight = () => setWeights((p) => [...p, { _k: nextKey(), label: "", pct: "" }]);
  const dropWeight = (k) => setWeights((p) => p.filter((w) => w._k !== k));

  const datelessCount = asns.filter((a) => a.title.trim() && !a.due.trim()).length
    + exams.filter((x) => x.title.trim() && !x.date.trim()).length;
  const weightTotal = weights.reduce((s, w) => s + (w.pct.trim() && Number.isFinite(Number(w.pct)) ? Number(w.pct) : 0), 0);
  const canSave = !busy && Boolean(course.name.trim()) && Boolean(course.term.trim());

  async function save() {
    if (!canSave) return;
    setBusy(true); setError("");
    const payload = {
      course: { name: course.name.trim(), term: course.term.trim() },
      assignments: asns.filter((a) => a.title.trim()).map((a) => ({
        title: a.title.trim(),
        ...(a.due.trim() ? { dueDate: a.due.trim() } : {}),
        ...(a.weight.trim() ? { weight: Number(a.weight) } : {}), // empty -> omitted, never 0
      })),
      exams: exams.filter((x) => x.title.trim()).map((x) => ({
        title: x.title.trim(),
        ...(x.date.trim() ? { date: x.date.trim() } : {}),
        ...(x.type.trim() ? { type: x.type.trim() } : {}),
        ...(x.weight.trim() ? { weight: Number(x.weight) } : {}),
      })),
      // a weight row needs BOTH a label and a non-empty pct — a blank row is dropped, never sent as 0
      gradingWeights: weights.filter((w) => w.label.trim() && w.pct.trim()).map((w) => ({ label: w.label.trim(), pct: Number(w.pct) })),
    };
    try {
      const r = await api.syllabus.save(payload);
      if (r.ok) { onDone(); return; }
      if (r.status === 409) {
        setError("You already have a term by that name — rename the course or term, or delete the old one first.");
      } else if (r.status === 400) {
        setError("Add a course name and term before saving.");
      } else if (r.status === 401) {
        setError("Your session expired — sign in again.");
      } else {
        setError("Couldn't save this term — give it another try.");
      }
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally { setBusy(false); }
  }

  if (phase === "preview") {
    return (
      <div className="study syllabus-intake">
        <header className="study-top">
          <button className="link" onClick={() => { setPhase("compose"); setError(""); }}>← Back</button>
        </header>
        <h1 className="study-head">Check &amp; save</h1>
        {error && <p className="error" role="alert">{error}</p>}

        <section className="syl-sec">
          <h2>Course</h2>
          <div className="syl-row">
            <input value={course.name} placeholder="Course name" aria-label="Course name"
              onChange={(e) => setCourse({ ...course, name: e.target.value })} />
            <input value={course.term} placeholder="Term" aria-label="Term"
              onChange={(e) => setCourse({ ...course, term: e.target.value })} />
          </div>
        </section>

        <section className="syl-sec">
          <h2>Assignments</h2>
          {asns.map((a) => (
            <div key={a._k} className="syl-row">
              <input value={a.title} placeholder="Title" aria-label="Assignment title" onChange={(e) => editAsn(a._k, "title", e.target.value)} />
              <input type="date" value={a.due} aria-label="Due date" onChange={(e) => editAsn(a._k, "due", e.target.value)} />
              <input type="number" className="syl-weight" value={a.weight} placeholder="wt %" aria-label="Assignment weight percent" onChange={(e) => editAsn(a._k, "weight", e.target.value)} />
              <button className="link" onClick={() => dropAsn(a._k)}>Remove</button>
            </div>
          ))}
          <button className="link" onClick={addAsn}>+ Add assignment</button>
        </section>

        <section className="syl-sec">
          <h2>Exams</h2>
          {exams.map((x) => (
            <div key={x._k} className="syl-row">
              <input value={x.title} placeholder="Title" aria-label="Exam title" onChange={(e) => editExam(x._k, "title", e.target.value)} />
              <input type="date" value={x.date} aria-label="Exam date" onChange={(e) => editExam(x._k, "date", e.target.value)} />
              <input value={x.type} placeholder="type" className="syl-type" aria-label="Exam type" onChange={(e) => editExam(x._k, "type", e.target.value)} />
              <input type="number" className="syl-weight" value={x.weight} placeholder="wt %" aria-label="Exam weight percent" onChange={(e) => editExam(x._k, "weight", e.target.value)} />
              <button className="link" onClick={() => dropExam(x._k)}>Remove</button>
            </div>
          ))}
          <button className="link" onClick={addExam}>+ Add exam</button>
        </section>

        <section className="syl-sec">
          <h2>Grading weights</h2>
          {weights.map((w) => (
            <div key={w._k} className="syl-row">
              <input value={w.label} placeholder="Category (e.g. Exams)" aria-label="Weight label" onChange={(e) => editWeight(w._k, "label", e.target.value)} />
              <input type="number" className="syl-weight" value={w.pct} placeholder="%" aria-label="Weight percent" onChange={(e) => editWeight(w._k, "pct", e.target.value)} />
              <button className="link" onClick={() => dropWeight(w._k)}>Remove</button>
            </div>
          ))}
          <button className="link" onClick={addWeight}>+ Add weight</button>
          <p className="muted syl-total">Total {weightTotal}%{weightTotal !== 100 ? " — partial is fine" : ""}</p>
        </section>

        {datelessCount > 0 && (
          <p className="muted syl-dateless" role="note">
            {datelessCount} item{datelessCount === 1 ? "" : "s"} {datelessCount === 1 ? "has" : "have"} no date yet — add {datelessCount === 1 ? "one" : "dates"} to include {datelessCount === 1 ? "it" : "them"}, or {datelessCount === 1 ? "it" : "they"} won't be saved.
          </p>
        )}

        <div className="cardform-actions">
          <button className="btn" onClick={save} disabled={!canSave}>{busy ? "Saving…" : "Save term"}</button>
          <button className="link" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="study syllabus-intake">
      <header className="study-top">
        <button className="link" onClick={onCancel}>← Courses</button>
      </header>
      <h1 className="study-head">Import a syllabus</h1>
      <div className="cardform">
        <p className="muted">Drop your syllabus PDF — I'll pull out the assignments, exam dates, and grading weights for you to check before anything is saved.</p>
        <div className="gen-source">
          {attach
            ? <span className="gen-chip">📄 {attach.name} · {fmtBytes(b64Bytes(attach.data))}
                <button className="link" onClick={removeAttach}>Remove</button></span>
            : <label className="gen-photo-add"><span className="link">Choose a PDF</span>
                <input type="file" accept="application/pdf" onChange={onPickPdf} style={{ display: "none" }} /></label>}
        </div>
        <label className="field"><span>Focus (optional)</span>
          <textarea rows={2} value={guide} placeholder="Optional — tell me what to focus on (e.g. “just the exam schedule”)"
            onChange={(e) => setGuide(e.target.value)} /></label>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="cardform-actions">
          <button className="btn" onClick={readSyllabus} disabled={!attach || busy}>{busy ? "Reading…" : "Read syllabus"}</button>
          <button className="link" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// SCH·4 — the weekly class-schedule grid (Mon–Fri). Dylan is the primary user (his D1 timetable).
// Pure display; records + arithmetic, no srs.
const GRID_DAYS = [["MO", "Mon"], ["TU", "Tue"], ["WE", "Wed"], ["TH", "Thu"], ["FR", "Fri"]];
const DAY_LABEL = { MO: "Mon", TU: "Tue", WE: "Wed", TH: "Thu", FR: "Fri", SA: "Sat", SU: "Sun" };
function fmt12(hhmm) {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || "");
  if (!m) return hhmm || "";
  let h = Number(m[1]); const ap = h < 12 ? "am" : "pm";
  h = h % 12 || 12;
  return m[2] === "00" ? `${h}${ap}` : `${h}:${m[2]}${ap}`;
}
function WeeklyGrid({ meetings }) {
  const byDay = (d) => meetings.filter((m) => m.day === d).sort((a, b) => a.start.localeCompare(b.start));
  const weekend = meetings.filter((m) => m.day === "SA" || m.day === "SU");
  return (
    <div className="week-grid" role="table" aria-label="Weekly class schedule">
      {GRID_DAYS.map(([code, label]) => (
        <div key={code} className="week-col" role="column">
          <div className="week-day">{label}</div>
          {byDay(code).length === 0
            ? <p className="muted week-empty">—</p>
            : byDay(code).map((m) => (
                <div key={m.id} className={`week-slot week-${m.kind}`}>
                  <div className="week-course">{m.course || "Class"}</div>
                  <div className="week-time">{fmt12(m.start)}–{fmt12(m.end)}</div>
                  {m.room && <div className="week-room">{m.room}</div>}
                </div>
              ))}
        </div>
      ))}
      {weekend.length > 0 && (
        <p className="muted week-weekend">Also: {weekend.map((m) => `${DAY_LABEL[m.day]} ${fmt12(m.start)} ${m.course || "Class"}`).join(" · ")}</p>
      )}
    </div>
  );
}

// SCH·4 — the class-schedule intake (paste accessible text → free/deterministic, or drop a PDF).
// Writes recurring `meetings` on Save — records only, his zero-srs seam is untouched.
const MEETING_KINDS = ["lecture", "lab", "office-hours", "other"];
function ScheduleIntake({ onDone, onCancel }) {
  const [phase, setPhase] = useState("compose");
  const [pasted, setPasted] = useState("");
  const [attach, setAttach] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [meetings, setMeetings] = useState([]);
  const [termStart, setTermStart] = useState("");
  const [termEnd, setTermEnd] = useState("");
  const keyRef = useRef(0);
  const nextKey = () => (keyRef.current += 1);

  async function onPickPdf(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = "";
    if (!file) return;
    setBusy(true); setError("");
    try {
      const { data } = await readPdf(file);
      setAttach({ name: file.name, mediaType: "application/pdf", data });
    } catch {
      setError("Couldn't read that PDF — try a different file.");
    } finally { setBusy(false); }
  }
  function removeAttach() { setAttach(null); setError(""); }

  function loadPreview(p) {
    setMeetings((p.meetings || []).map((m) => ({
      _k: nextKey(), day: m.day || "MO", start: m.start || "", end: m.end || "",
      course: m.course || "", room: m.room || "", instructor: m.instructor || "", kind: m.kind || "lecture",
    })));
    if (p.termStart) setTermStart(p.termStart);
    if (p.termEnd) setTermEnd(p.termEnd);
  }

  async function readSchedule() {
    if (busy) return;
    const text = pasted.trim();
    if (!text && !attach) return;
    setBusy(true); setError("");
    try {
      const r = text ? await api.schedule.parseText(text)
                     : await api.schedule.parsePdf({ mediaType: attach.mediaType, data: attach.data });
      const code = r.data?.code;
      if (r.ok && r.data?.deferred) {
        setError("Couldn't read this one — try again.");
      } else if (r.ok && r.data?.preview && r.data.preview.meetings?.length) {
        loadPreview(r.data.preview);
        setPhase("preview");
      } else if (r.ok && r.data?.preview) {
        setError("I couldn't find any class times in that — check the text or try the PDF.");
      } else if (r.status === 415 || code === "unsupported") {
        setError("That file isn't a readable PDF — export or save it as a PDF.");
      } else if (code === "too_big" || r.status === 413) {
        setError("That PDF is too large to upload — try fewer pages or split it.");
      } else if (r.status === 401) {
        setError("Your session expired — sign in again.");
      } else {
        setError("Something went wrong reading that schedule. Give it another try.");
      }
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally { setBusy(false); }
  }

  const editMtg = (k, f, v) => setMeetings((p) => p.map((m) => (m._k === k ? { ...m, [f]: v } : m)));
  const addMtg = () => setMeetings((p) => [...p, { _k: nextKey(), day: "MO", start: "", end: "", course: "", room: "", instructor: "", kind: "lecture" }]);
  const dropMtg = (k) => setMeetings((p) => p.filter((m) => m._k !== k));

  const isHHMM = (t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t);
  const validRows = meetings.filter((m) => m.day && isHHMM(m.start) && isHHMM(m.end));
  const gridMeetings = validRows.map((m) => ({ id: `k${m._k}`, day: m.day, start: m.start, end: m.end, kind: m.kind, course: m.course, room: m.room }));
  const canSave = !busy && validRows.length > 0 && Boolean(termStart) && Boolean(termEnd) && termEnd >= termStart;

  async function save() {
    if (!canSave) return;
    setBusy(true); setError("");
    const payload = {
      meetings: validRows.map((m) => ({
        day: m.day, start: m.start, end: m.end, kind: m.kind,
        ...(m.course.trim() ? { course: m.course.trim() } : {}),
        ...(m.room.trim() ? { room: m.room.trim() } : {}),
        ...(m.instructor.trim() ? { instructor: m.instructor.trim() } : {}),
      })),
      termStart, termEnd,
    };
    try {
      const r = await api.schedule.save(payload);
      if (r.ok) { onDone(); return; }
      if (r.status === 400) setError("Add term start and end dates, and at least one class time.");
      else if (r.status === 401) setError("Your session expired — sign in again.");
      else setError("Couldn't save this schedule — give it another try.");
    } catch {
      setError("Couldn't reach the server — check your connection and retry.");
    } finally { setBusy(false); }
  }

  if (phase === "preview") {
    return (
      <div className="study schedule-intake">
        <header className="study-top">
          <button className="link" onClick={() => { setPhase("compose"); setError(""); }}>← Back</button>
        </header>
        <h1 className="study-head">Check &amp; save your schedule</h1>
        {error && <p className="error" role="alert">{error}</p>}

        <section className="syl-sec">
          <h2>Term dates</h2>
          <div className="syl-row">
            <label className="field"><span>Term starts</span>
              <input type="date" value={termStart} aria-label="Term start" onChange={(e) => setTermStart(e.target.value)} /></label>
            <label className="field"><span>Term ends</span>
              <input type="date" value={termEnd} aria-label="Term end" onChange={(e) => setTermEnd(e.target.value)} /></label>
          </div>
          <p className="muted">Classes repeat weekly between these dates.</p>
        </section>

        <section className="syl-sec">
          <h2>Meetings</h2>
          {meetings.map((m) => (
            <div key={m._k} className="syl-row sched-row">
              <select value={m.day} aria-label="Day" onChange={(e) => editMtg(m._k, "day", e.target.value)}>
                {Object.entries(DAY_LABEL).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
              <input type="time" value={m.start} aria-label="Start time" onChange={(e) => editMtg(m._k, "start", e.target.value)} />
              <input type="time" value={m.end} aria-label="End time" onChange={(e) => editMtg(m._k, "end", e.target.value)} />
              <input value={m.course} placeholder="Class" aria-label="Course" onChange={(e) => editMtg(m._k, "course", e.target.value)} />
              <input value={m.room} placeholder="Room" className="sched-room" aria-label="Room" onChange={(e) => editMtg(m._k, "room", e.target.value)} />
              <select value={m.kind} aria-label="Kind" onChange={(e) => editMtg(m._k, "kind", e.target.value)}>
                {MEETING_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <button className="link" onClick={() => dropMtg(m._k)}>Remove</button>
            </div>
          ))}
          <button className="link" onClick={addMtg}>+ Add a class</button>
        </section>

        {gridMeetings.length > 0 && (
          <section className="syl-sec">
            <h2>Your week</h2>
            <WeeklyGrid meetings={gridMeetings} />
          </section>
        )}

        <div className="cardform-actions">
          <button className="btn" onClick={save} disabled={!canSave}>{busy ? "Saving…" : "Save schedule"}</button>
          <button className="link" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="study schedule-intake">
      <header className="study-top">
        <button className="link" onClick={onCancel}>← Courses</button>
      </header>
      <h1 className="study-head">Add your class schedule</h1>
      <div className="cardform">
        <p className="muted">Paste your timetable's text (the "accessible text" version reads best), or drop the PDF — I'll pull out your weekly classes for you to check before anything is saved.</p>
        <label className="field"><span>Paste timetable text</span>
          <textarea rows={5} value={pasted} placeholder={"e.g.\nMonday: 8–9:30 a.m.: Gross Anatomy, Dr. Reyes, Room 201\nTuesday: 1–2:30 p.m.: Histology Lab, Lab 5"}
            onChange={(e) => setPasted(e.target.value)} /></label>
        <div className="gen-source">
          {attach
            ? <span className="gen-chip">📄 {attach.name} · {fmtBytes(b64Bytes(attach.data))}
                <button className="link" onClick={removeAttach}>Remove</button></span>
            : <label className="gen-photo-add"><span className="link">…or choose a PDF</span>
                <input type="file" accept="application/pdf" onChange={onPickPdf} style={{ display: "none" }} /></label>}
        </div>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="cardform-actions">
          <button className="btn" onClick={readSchedule} disabled={(!pasted.trim() && !attach) || busy}>{busy ? "Reading…" : "Read schedule"}</button>
          <button className="link" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// I·3 — the calendar subscription feed: a secret link a phone's calendar app polls. The server
// shows the full link ONCE (at mint); Regenerate revokes the old link (audited server-side).
function CalendarFeedRow() {
  const [feed, setFeed] = useState(null);     // { configured } from the server (never the token)
  const [minted, setMinted] = useState(null); // the one-time full URL after a regenerate
  const [busy, setBusy] = useState(false);
  useEffect(() => { req("GET", "/api/calendar/feed").then((r) => setFeed(r.ok ? r.data : { configured: false })); }, []);
  async function regen() {
    setBusy(true);
    try {
      const r = await req("POST", "/api/calendar/feed/regenerate");
      if (r.ok) { setMinted(window.location.origin + r.data.url); setFeed({ configured: true }); }
    } finally { setBusy(false); }
  }
  return (
    <div className="calendar-feed">
      {minted
        ? <p className="feed-url">Subscribe link — copy it now, it's shown once:<br /><code>{minted}</code></p>
        : <p className="muted">
            {feed?.configured
              ? "Calendar feed is active. Regenerating revokes the current link."
              : "Create a private link your calendar app can subscribe to."}
          </p>}
      <button className="link" onClick={regen} disabled={busy}>
        {feed?.configured ? "Regenerate feed link" : "Create feed link"}
      </button>
    </div>
  );
}

// ── CAL·6b — the on-screen month calendar (ported from the girls' CAL·3/4; Dylan's tokens) ─────────
// Reads already-synced HUB data (courses/assignments/exams/meetings) + his personal events and flattens
// them through core's expandOccurrences. NO srs anywhere — his review surface is Anki, untouched. Grid
// date math is UTC + tz-free; "today" is the ONLY thing that reads local (localDateStr) — the view's job.
const CAL_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const CAL_DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CAL_CHIP_CAP = 3; // chips shown per cell before the "+N more" indicator
const CAL_WEEKDAYS = [["MO", "Monday"], ["TU", "Tuesday"], ["WE", "Wednesday"], ["TH", "Thursday"], ["FR", "Friday"], ["SA", "Saturday"], ["SU", "Sunday"]];

const calYmd = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;

// A 6-week (42-cell), Sunday-first grid for {year, month(0-11)}. Leading/trailing days belong to the
// adjacent months (inMonth:false → styled faded). Built off UTC so the run machine's tz never shifts a cell.
function calMonthGrid(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const gridStart = new Date(Date.UTC(year, month, 1 - first.getUTCDay())); // back up to the Sunday on/before the 1st
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart.getTime() + i * 86_400_000);
    cells.push({ date: calYmd(d), inMonth: d.getUTCMonth() === month });
  }
  return cells;
}
function calShiftMonth({ year, month }, delta) {
  const m = month + delta;
  return { year: year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
}
function calFmt12(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}
function calOccTime(o) {
  if (o.allDay || !o.start) return "All day";
  return o.end ? `${calFmt12(o.start)} – ${calFmt12(o.end)}` : calFmt12(o.start);
}
function calLongDate(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return `${CAL_MONTHS[m - 1]} ${d}, ${y}`;
}

// The inline Add/Edit form for a personal event, revealed inside the day-detail panel (mirrors the
// Courses `course-form` reveal, not a modal). Save is gated by core's validateEvent — the SAME rules
// the server enforces; the server stays authoritative (a 400 surfaces inline). onSaved() refetches events.
function EventForm({ day, event, latestTermEnd, onSaved, onCancel }) {
  const editing = !!event;
  const [title, setTitle] = useState(event?.title ?? "");
  const [kind, setKind] = useState(event?.kind ?? "activity");
  const [repeats, setRepeats] = useState(event && event.days ? "weekly" : "once");
  const [date, setDate] = useState(event?.date ?? day);
  const [days, setDays] = useState(event?.days ?? []);
  const [from, setFrom] = useState(event?.from ?? day);
  const [to, setTo] = useState(event?.to ?? (editing ? "" : (latestTermEnd ?? "")));
  const [allDay, setAllDay] = useState(editing ? !event.start : true);
  const [start, setStart] = useState(event?.start ?? "");
  const [end, setEnd] = useState(event?.end ?? "");
  const [color, setColor] = useState(event?.color ?? "");
  const [note, setNote] = useState(event?.note ?? "");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // The create-shaped payload — ONLY the fields for the chosen mode. Gates Save (validateEvent) AND, on add, is POSTed.
  function buildFields() {
    const p = { title: title.trim(), kind };
    if (repeats === "once") p.date = date;
    else { p.days = days; p.from = from; p.to = to; }
    if (!allDay) { p.start = start; p.end = end; }
    if (color) p.color = color;
    if (note.trim()) p.note = note.trim();
    return p;
  }
  const valid = !validateEvent(buildFields()).error;

  async function save() {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    const f = buildFields();
    let r;
    if (editing) {
      // null-drop the fields the chosen mode no longer uses (patchRecord semantics) so a once↔weekly
      // or timed↔all-day switch clears the stale keys server-side.
      r = await api.events.update(event.id, {
        title: f.title, kind: f.kind,
        date: f.date ?? null, days: f.days ?? null, from: f.from ?? null, to: f.to ?? null,
        start: f.start ?? null, end: f.end ?? null, color: f.color ?? null, note: f.note ?? null,
      });
    } else {
      r = await api.events.create(f);
    }
    setBusy(false);
    if (r && r.ok) onSaved();
    else setErr((r && r.data && r.data.error) || "Could not save — try again.");
  }

  const toggleDay = (code) => setDays((ds) => (ds.includes(code) ? ds.filter((d) => d !== code) : [...ds, code]));

  return (
    <div className="course-form cal-form">
      <input value={title} placeholder="Event title" autoFocus aria-label="Event title" onChange={(e) => setTitle(e.target.value)} />
      <select aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
        {EVENT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
      <select aria-label="Repeats" value={repeats} onChange={(e) => setRepeats(e.target.value)}>
        <option value="once">Just once</option>
        <option value="weekly">Repeats weekly</option>
      </select>
      {repeats === "once"
        ? <input type="date" aria-label="Date" value={date} onChange={(e) => setDate(e.target.value)} />
        : <>
            <span className="cal-days" role="group" aria-label="Days">
              {CAL_WEEKDAYS.map(([code, name]) => (
                <button key={code} type="button" aria-label={name}
                  className={days.includes(code) ? "day-toggle is-on" : "day-toggle"}
                  onClick={() => toggleDay(code)}>{code}</button>
              ))}
            </span>
            <input type="date" aria-label="Repeat from" value={from} onChange={(e) => setFrom(e.target.value)} />
            <input type="date" aria-label="Repeat until" value={to} onChange={(e) => setTo(e.target.value)} />
          </>}
      <label className="cal-allday">
        <input type="checkbox" aria-label="All day" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} /> All day
      </label>
      {!allDay && (
        <span className="cal-times">
          <input type="time" aria-label="Start time" value={start} onChange={(e) => setStart(e.target.value)} />
          <input type="time" aria-label="End time" value={end} onChange={(e) => setEnd(e.target.value)} />
        </span>
      )}
      <span className="swatches" role="group" aria-label="Color">
        {COURSE_COLORS.map((hex) => (
          <button key={hex} type="button" className={color === hex ? "swatch is-picked" : "swatch"}
            style={{ background: hex }} aria-label={`Color ${hex}`}
            onClick={() => setColor(color === hex ? "" : hex)} />
        ))}
      </span>
      <input value={note} placeholder="Note (optional)" aria-label="Note" onChange={(e) => setNote(e.target.value)} />
      <button className="btn btn-sm" onClick={save} disabled={!valid || busy}>{editing ? "Save" : "Add event"}</button>
      <button className="link" onClick={onCancel}>Cancel</button>
      {err && <p className="cal-form-err del-warn" role="alert">{err}</p>}
    </div>
  );
}

function CalendarView({ onBack }) {
  const [data, setData] = useState(null); // { courses, assignments, exams, meetings, events } — fetched once
  const [selected, setSelected] = useState(null); // the tapped day (YYYY-MM-DD), or null
  const [form, setForm] = useState(null); // null | { event?: rawRecord } — the inline add/edit form
  const [confirmDel, setConfirmDel] = useState(null); // eventId pending delete-confirm
  const today = localDateStr();
  const [ym, setYm] = useState(() => {
    const [y, m] = today.split("-").map(Number);
    return { year: y, month: m - 1 };
  });

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.hub.courses(), api.hub.assignments(), api.exams.list(),
      api.schedule.meetings(), api.events.list(),
    ]).then(([courses, assignments, exams, meetings, events]) => {
      if (alive) setData({ courses, assignments, exams, meetings, events });
    });
    return () => { alive = false; };
  }, []);

  if (!data) {
    return (
      <div className="study cal">
        <header className="study-top"><button className="link" onClick={onBack}>← Dashboard</button></header>
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const cells = calMonthGrid(ym.year, ym.month);
  // Occurrences across the WHOLE visible grid span. Courses are NOT passed to expandOccurrences — they
  // feed the color map only (the engine stays color-policy-free).
  const occ = expandOccurrences({
    meetings: data.meetings, events: data.events, assignments: data.assignments, exams: data.exams,
    from: cells[0].date, to: cells[41].date,
  });
  const byDate = new Map();
  for (const o of occ) {
    if (!byDate.has(o.date)) byDate.set(o.date, []);
    byDate.get(o.date).push(o);
  }
  // COLOR RESOLUTION: course-linked items inherit the course color; personal events use the color he
  // picked; anything else gets a neutral class (no inline color, never a guessed default).
  const courseColor = new Map(data.courses.filter((c) => c.color).map((c) => [c.id, c.color]));
  const colorFor = (o) => (o.courseId && courseColor.has(o.courseId)) ? courseColor.get(o.courseId) : (o.color || null);

  const selectedOcc = selected ? (byDate.get(selected) ?? []) : null;
  const goToday = () => { const [y, m] = today.split("-").map(Number); setYm({ year: y, month: m - 1 }); };
  // A write refetches ONLY events; courses/assignments/exams/meetings state is left untouched (the grid recomputes).
  const refreshEvents = () => api.events.list().then((events) => setData((d) => ({ ...d, events })));
  const closeForm = () => { setForm(null); setConfirmDel(null); };
  const pickDay = (date) => { setSelected(date); closeForm(); };
  const latestTermEnd = data.meetings.map((m) => m.termEnd).filter(Boolean).sort().at(-1) ?? "";

  return (
    <div className="study cal">
      <header className="study-top"><button className="link" onClick={onBack}>← Dashboard</button></header>
      <div className="study-head">
        <h1 className="cal-heading">{CAL_MONTHS[ym.month]} {ym.year}</h1>
        <span className="cal-nav">
          <button className="btn btn-sm btn-ghost" onClick={() => setYm(calShiftMonth(ym, -1))} aria-label="Previous month">‹ Prev</button>
          <button className="btn btn-sm btn-ghost" onClick={goToday}>Today</button>
          <button className="btn btn-sm btn-ghost" onClick={() => setYm(calShiftMonth(ym, 1))} aria-label="Next month">Next ›</button>
        </span>
      </div>
      <div className="cal-dow" aria-hidden="true">
        {CAL_DOW.map((d) => <span key={d} className="cal-dow-cell">{d}</span>)}
      </div>
      <div className="cal-grid" role="grid">
        {cells.map((cell) => {
          const items = byDate.get(cell.date) ?? [];
          const shown = items.slice(0, CAL_CHIP_CAP);
          const overflow = items.length - shown.length;
          const cls = ["cal-cell"];
          if (!cell.inMonth) cls.push("is-faded");
          if (cell.date === today) cls.push("is-today");
          if (cell.date === selected) cls.push("is-selected");
          return (
            <button type="button" key={cell.date} className={cls.join(" ")} data-date={cell.date}
              onClick={() => pickDay(cell.date)} aria-label={cell.date}>
              <span className="cal-daynum">{Number(cell.date.slice(8, 10))}</span>
              <span className="cal-chips">
                {shown.map((o, i) => {
                  const color = colorFor(o);
                  return (
                    <span key={o.source + o.id + i} className={color ? "cal-chip" : "cal-chip is-neutral"}
                      style={color ? { borderLeftColor: color } : undefined} data-source={o.source} data-id={o.id}>
                      <span className="cal-chip-dot" style={color ? { background: color } : undefined} aria-hidden="true" />
                      <span className="cal-chip-title">{o.title}</span>
                    </span>
                  );
                })}
                {overflow > 0 && <span className="cal-more">+{overflow} more</span>}
              </span>
            </button>
          );
        })}
      </div>
      {selected && (
        <section className="cal-detail">
          <div className="cal-detail-head">
            <h2>{calLongDate(selected)}</h2>
            {!form && <button className="btn btn-sm" onClick={() => setForm({})}>+ Add</button>}
          </div>
          {form && (
            <EventForm day={selected} event={form.event} latestTermEnd={latestTermEnd}
              onSaved={() => { closeForm(); refreshEvents(); }} onCancel={closeForm} />
          )}
          {selectedOcc.length === 0 && !form
            ? <p className="muted">Nothing scheduled.</p>
            : <ul className="cal-detail-list">
                {selectedOcc.map((o, i) => {
                  const color = colorFor(o);
                  const isEvent = o.source === "event"; // ONLY personal events are editable here — courses/assignments/exams/classes are read-only on the calendar
                  return (
                    <li key={o.source + o.id + i} className="cal-detail-row" data-source={o.source} data-id={o.id}>
                      <span className="cal-chip-dot" style={color ? { background: color } : undefined} aria-hidden="true" />
                      <span className="cal-detail-title">{o.title}</span>
                      <span className="cal-detail-time">{calOccTime(o)}</span>
                      {isEvent && confirmDel !== o.id && (
                        <span className="asn-actions">
                          <button className="link" onClick={() => setForm({ event: data.events.find((e) => e.id === o.id) })}>Edit</button>
                          <button className="link" onClick={() => setConfirmDel(o.id)}>Delete</button>
                        </span>
                      )}
                      {isEvent && confirmDel === o.id && (
                        <span className="del-warn cal-del" role="alert">
                          Delete this event?{" "}
                          <button className="link" onClick={async () => { await api.events.remove(o.id); setConfirmDel(null); refreshEvents(); }}>Delete</button>{" "}
                          <button className="link" onClick={() => setConfirmDel(null)}>Keep</button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>}
        </section>
      )}
    </div>
  );
}

function CoursesView({ onBack }) {
  const [courses, setCourses] = useState(null);
  const [asns, setAsns] = useState(null);
  const [exams, setExams] = useState([]); // E·4 — exams by course
  const [term, setTerm] = useState(null); // null = auto (latest created course's term)
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false); // Y·4: the syllabus intake screen
  const [schedIntake, setSchedIntake] = useState(false); // SCH·4: the class-schedule intake screen
  const [meetings, setMeetings] = useState([]); // SCH·4: saved weekly meetings (for the grid)
  const [cName, setCName] = useState("");
  const [cTerm, setCTerm] = useState("");
  const [cColor, setCColor] = useState("");

  function refresh() {
    api.exams.list().then(setExams); // E·4 — exams ride alongside (empty stays empty; no gate on them)
    api.schedule.meetings().then(setMeetings); // SCH·4 — the weekly grid (empty stays empty)
    return Promise.all([api.hub.courses().then(setCourses), api.hub.assignments().then(setAsns)]);
  }
  useEffect(() => { refresh(); }, []);

  // Y·4 — a saved syllabus lands a whole term; on done, refresh so the new course/assignments show.
  if (importing) return <SyllabusIntake onDone={() => { setImporting(false); refresh(); }} onCancel={() => setImporting(false)} />;
  // SCH·4 — a saved schedule lands the week's recurring meetings; on done, refresh so the grid shows.
  if (schedIntake) return <ScheduleIntake onDone={() => { setSchedIntake(false); refresh(); }} onCancel={() => setSchedIntake(false)} />;

  if (courses === null || asns === null) {
    return <div className="study"><p className="muted">Loading…</p></div>;
  }

  // ARC·1 — the term flow is ACTIVE courses only; archived ones (any term) sit in their own
  // collapsed section below, and never drive the term switcher or the "latest term" default.
  const active = activeCourses(courses);
  const archived = archivedCourses(courses);
  const terms = [...new Set(active.map((c) => c.term))];
  // BIG·1b: ONE shared total order in core decides this (createdAt, then id — nothing can tie).
  // A same-millisecond import is the normal case for a Canvas sync, and the old inline comparator
  // never returned 0, so its answer moved with the order the courses arrived in.
  const latest = latestTerm(active);
  const activeTerm = term ?? latest;
  const termCourses = active.filter((c) => c.term === activeTerm);
  // X·4: the lookup spans ALL terms, not the term-filtered list — renaming a course's term must
  // not strand its open assignments out of Upcoming/Overdue while the dashboard tile (which counts
  // every term) still counts them. Tile and view agree: open assignments of any existing course.
  const courseById = Object.fromEntries(courses.map((c) => [c.id, c]));
  const today = localDateStr();
  const horizon = addDays(14); // Upcoming lean: a two-week lookahead (a 7-day window hides next week's practical)
  const openAsns = forActiveCourses(asns, courses).filter((a) => courseById[a.courseId] && !a.done); // ARC·1 — an archived course's work leaves Upcoming/Overdue
  const overdue = openAsns.filter((a) => a.due < today).sort(byDue);
  const upcoming = openAsns.filter((a) => a.due >= today && a.due <= horizon).sort(byDue);

  async function createCourse() {
    if (!cName.trim() || !cTerm.trim()) return;
    await api.hub.createCourse(cName.trim(), cTerm.trim(), cColor || undefined);
    setCName(""); setCColor(""); setCreating(false); refresh();
  }

  return (
    <div className="study hub">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
        {terms.length > 1 && (
          <select className="term-switch" value={activeTerm} onChange={(e) => setTerm(e.target.value)} aria-label="Term">
            {terms.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </header>
      <div className="study-head">
        <h1>Courses &amp; Assignments</h1>
        {!creating
          ? <span className="hub-actions">
              <button className="btn btn-sm" onClick={() => { setCTerm(activeTerm ?? "Fall 2026"); setCreating(true); }}>+ New course</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setImporting(true)}>Import syllabus</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setSchedIntake(true)}>Class schedule</button>
            </span>
          : <div className="newdeck course-form">
              <input value={cName} placeholder="Course name" autoFocus onChange={(e) => setCName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") createCourse(); }} aria-label="Course name" />
              <input value={cTerm} placeholder="Term" onChange={(e) => setCTerm(e.target.value)} aria-label="Term" />
              <span className="swatches" role="group" aria-label="Color">
                {COURSE_COLORS.map((hex) => (
                  <button key={hex} type="button"
                    className={cColor === hex ? "swatch is-picked" : "swatch"}
                    style={{ background: hex }} aria-label={`Color ${hex}`}
                    onClick={() => setCColor(cColor === hex ? "" : hex)} />
                ))}
              </span>
              <button className="btn btn-sm" onClick={createCourse} disabled={!cName.trim() || !cTerm.trim()}>Create</button>
              <button className="link" onClick={() => { setCreating(false); setCName(""); setCColor(""); }}>Cancel</button>
            </div>}
      </div>

      {termCourses.length === 0
        ? <p className="muted empty">No courses yet — add your D1 fall schedule.</p>
        : <>
            <p className="gpa-line">
              Term GPA <b>{fmtGpa(gpaOf(termCourses))}</b> · Cumulative <b>{fmtGpa(gpaOf(courses))}</b>
              {gpaOf(courses) === null && <span className="muted"> — no grades entered yet</span>}
            </p>

            <section className="upcoming">
              <h2>Upcoming · next 14 days</h2>
              {overdue.length === 0 && upcoming.length === 0
                ? <p className="muted">Nothing due in the next 14 days.</p>
                : <ul className="asn-list">
                    {[...overdue, ...upcoming].map((a) => (
                      <AssignmentRow key={a.id} asn={a} course={courseById[a.courseId]} showCourse onRefresh={refresh} />
                    ))}
                  </ul>}
            </section>

            <ul className="course-list">
              {termCourses.map((c) => (
                <CourseCard key={c.id} course={c}
                  assignments={asns.filter((a) => a.courseId === c.id)}
                  exams={exams.filter((x) => x.courseId === c.id)} onRefresh={refresh} />
              ))}
            </ul>
          </>}
      {/* ARC·1 — archived courses: every term, collapsed, gone entirely when there are none. Every
          byte is still here (assignments, exams, decks); the course just left the daily surfaces. */}
      {archived.length > 0 && (
        <details className="archived-courses">
          <summary>Archived ({archived.length})</summary>
          <ul className="course-list">
            {archived.map((c) => (
              <CourseCard key={c.id} course={c} archived
                assignments={asns.filter((a) => a.courseId === c.id)}
                exams={exams.filter((x) => x.courseId === c.id)} onRefresh={refresh} />
            ))}
          </ul>
        </details>
      )}
      {/* SCH·4 — the saved weekly class schedule (recurring meetings; rides the calendar feed) */}
      {meetings.length > 0 && (
        <section className="week-section">
          <div className="week-head"><h2>Weekly schedule</h2>
            <button className="link" onClick={() => setSchedIntake(true)}>Update</button></div>
          <WeeklyGrid meetings={meetings} />
        </section>
      )}
      {/* I·2 — the whole hub as a calendar file (server-emitted .ics; the browser saves it) */}
      <p className="calendar-export"><a href="/api/calendar.ics" download>Calendar export (.ics)</a></p>
      <CalendarFeedRow />
    </div>
  );
}

// --- A·3: the Hours Log (ported from Alivia's A·1, restyled to his UNMC tokens) ---------------
// His clinical/shadowing hours predate dental school and keep counting (residency and
// scholarship applications ask). Timer first, manual add as the honest fallback; categories
// seed with Clinical and Shadowing first — his most common entries.
const STARTER_CATEGORIES = ["Clinical", "Shadowing", "Research", "Volunteer", "Work"];

/** date+time local inputs -> full ISO instant (the server stores/derives the rest). */
const localToIso = (date, time) => new Date(`${date}T${time}`).toISOString();
const entryDate = (iso) => { const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`; };
const entryTime = (iso) => { const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getHours())}:${p(d.getMinutes())}`; };

function HoursDatalists({ categories, places }) {
  return (
    <>
      <datalist id="hours-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="hours-places">{places.map((p) => <option key={p} value={p} />)}</datalist>
    </>
  );
}

function TimerCard({ active, onChanged }) {
  const [cat, setCat] = useState("");
  const [place, setPlace] = useState("");
  const [act, setAct] = useState("");
  const [err, setErr] = useState("");
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000); // re-render; the readout comes from the clock seam
    return () => clearInterval(t);
  }, [active]);

  async function start() {
    setErr("");
    try {
      const r = await api.hours.start(cat.trim(), place.trim(), act.trim());
      if (!r.ok) { setErr(r.data?.error ?? "couldn't start the timer — try again"); return; }
      setCat(""); setPlace(""); setAct("");
      onChanged();
    } catch { setErr("couldn't reach the server — nothing changed"); }
  }
  async function stop() {
    try {
      const r = await api.hours.stop();
      if (r.ok) onChanged();
    } catch { /* the running card stays; he can retry */ }
  }

  if (active) {
    return (
      <section className="timer-card is-running">
        <div className="timer-live">
          <span className="timer-elapsed">{fmtElapsed(nowMs() - Date.parse(active.startedAt))}</span>
          <span className="timer-what">{active.activity || active.category} · {active.place}</span>
        </div>
        <button className="btn" onClick={stop}>Stop</button>
      </section>
    );
  }
  return (
    <section className="timer-card">
      <div className="timer-form">
        <input list="hours-cats" value={cat} placeholder="Category (Clinical…)" aria-label="Category"
          onChange={(e) => setCat(e.target.value)} />
        <input list="hours-places" value={place} placeholder="Place" aria-label="Place"
          onChange={(e) => setPlace(e.target.value)} />
        <input value={act} placeholder="Activity (optional)" aria-label="Activity"
          onChange={(e) => setAct(e.target.value)} />
        <button className="btn" onClick={start} disabled={!cat.trim() || !place.trim()}>Start</button>
      </div>
      {err && <p className="deck-err">{err}</p>}
    </section>
  );
}

function ManualEntryForm({ onDone, onCancel }) {
  const [cat, setCat] = useState("");
  const [place, setPlace] = useState("");
  const [act, setAct] = useState("");
  const [date, setDate] = useState(localDateStr());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const ready = cat.trim() && place.trim() && date && from && to;

  async function save() {
    setErr("");
    try {
      const r = await api.hours.add({
        category: cat.trim(), place: place.trim(), activity: act.trim(),
        startedAt: localToIso(date, from), endedAt: localToIso(date, to),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      if (!r.ok) { setErr(r.data?.error ?? "couldn't save that entry"); return; }
      onDone();
    } catch { setErr("couldn't reach the server — nothing changed"); }
  }
  return (
    <div className="newdeck hours-form">
      <input list="hours-cats" value={cat} placeholder="Category" aria-label="Category" autoFocus onChange={(e) => setCat(e.target.value)} />
      <input list="hours-places" value={place} placeholder="Place" aria-label="Place" onChange={(e) => setPlace(e.target.value)} />
      <input value={act} placeholder="Activity (optional)" aria-label="Activity" onChange={(e) => setAct(e.target.value)} />
      <input type="date" value={date} aria-label="Date" onChange={(e) => setDate(e.target.value)} />
      <input type="time" value={from} aria-label="Start time" onChange={(e) => setFrom(e.target.value)} />
      <input type="time" value={to} aria-label="End time" onChange={(e) => setTo(e.target.value)} />
      <input value={note} placeholder="Note (optional)" aria-label="Note" onChange={(e) => setNote(e.target.value)} />
      <button className="btn btn-sm" onClick={save} disabled={!ready}>Save entry</button>
      <button className="link" onClick={onCancel}>Cancel</button>
      {err && <p className="deck-err">{err}</p>}
    </div>
  );
}

function HoursEntryRow({ e, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cat, setCat] = useState(e.category);
  const [place, setPlace] = useState(e.place);
  const [act, setAct] = useState(e.activity ?? "");
  const [date, setDate] = useState(entryDate(e.startedAt));
  const [from, setFrom] = useState(entryTime(e.startedAt));
  const [to, setTo] = useState(entryTime(e.endedAt));
  const [note, setNote] = useState(e.note ?? "");
  const [err, setErr] = useState("");

  async function save() {
    setErr("");
    const r = await api.hours.update(e.id, {
      category: cat.trim(), place: place.trim(), activity: act.trim(),
      startedAt: localToIso(date, from), endedAt: localToIso(date, to),
      note: note.trim() ? note.trim() : null,
    });
    if (!r.ok) { setErr(r.data?.error ?? "couldn't save the change"); return; }
    setEditing(false);
    onRefresh();
  }
  async function del() {
    const r = await api.hours.remove(e.id);
    if (r.ok) onRefresh();
  }

  if (editing) {
    return (
      <li className="asn-row hours-row">
        <div className="newdeck hours-form">
          <input list="hours-cats" value={cat} aria-label="Category" onChange={(ev) => setCat(ev.target.value)} />
          <input list="hours-places" value={place} aria-label="Place" onChange={(ev) => setPlace(ev.target.value)} />
          <input value={act} placeholder="Activity" aria-label="Activity" onChange={(ev) => setAct(ev.target.value)} />
          <input type="date" value={date} aria-label="Date" onChange={(ev) => setDate(ev.target.value)} />
          <input type="time" value={from} aria-label="Start time" onChange={(ev) => setFrom(ev.target.value)} />
          <input type="time" value={to} aria-label="End time" onChange={(ev) => setTo(ev.target.value)} />
          <input value={note} placeholder="Note" aria-label="Note" onChange={(ev) => setNote(ev.target.value)} />
          <button className="btn btn-sm" onClick={save} disabled={!cat.trim() || !place.trim()}>Save</button>
          <button className="link" onClick={() => setEditing(false)}>Cancel</button>
          {err && <p className="deck-err">{err}</p>}
        </div>
      </li>
    );
  }
  return (
    <li className="asn-row hours-row">
      <span className="hours-when">{entryDate(e.startedAt)}</span>
      <span className="hours-dur">{fmtMins(e.durationMin)}</span>
      <span className="asn-title">{e.category} · {e.place}{e.activity ? ` — ${e.activity}` : ""}</span>
      {e.origin === "manual" && <span className="hours-origin" title="Entered by hand (not timed live)">manual</span>}
      {e.note && <span className="hours-note">{e.note}</span>}
      <span className="asn-actions">
        <button className="link" onClick={() => setEditing(true)}>Edit</button>
        <button className="link" onClick={() => setConfirming(true)}>Delete</button>
      </span>
      {confirming && (
        <p className="del-warn">
          Delete this {fmtMins(e.durationMin)} {e.category} entry? This can&apos;t be undone.{" "}
          <button className="link" onClick={del}>Delete it</button>{" "}
          <button className="link" onClick={() => setConfirming(false)}>Keep it</button>
        </p>
      )}
    </li>
  );
}

function HoursView({ onBack }) {
  const [entries, setEntries] = useState(null);
  const [active, setActive] = useState(null);
  const [adding, setAdding] = useState(false);

  function refresh() {
    return Promise.all([api.hours.list().then(setEntries), api.hours.active().then(setActive)]);
  }
  useEffect(() => { refresh(); }, []);

  if (entries === null) return <div className="study"><p className="muted">Loading…</p></div>;

  const categories = [...new Set([...entries.map((e) => e.category), ...STARTER_CATEGORIES])];
  const places = [...new Set(entries.map((e) => e.place))];
  const totals = {};
  for (const e of entries) totals[e.category] = (totals[e.category] || 0) + e.durationMin;

  function exportCsv() {
    const blob = new Blob([hoursCsv(entries)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `dylan-hours-${localDateStr()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="study hours">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
        {entries.length > 0 && <button className="link" onClick={exportCsv}>Export CSV</button>}
      </header>
      <div className="study-head">
        <h1>Hours Log</h1>
        {!adding && <button className="btn btn-sm" onClick={() => setAdding(true)}>+ Add entry</button>}
      </div>
      <HoursDatalists categories={categories} places={places} />
      <TimerCard active={active} onChanged={refresh} />
      {adding && <ManualEntryForm onDone={() => { setAdding(false); refresh(); }} onCancel={() => setAdding(false)} />}
      {Object.keys(totals).length > 0 && (
        <section className="hours-totals">
          <h2>Totals</h2>
          <ul className="totals-list">
            {Object.entries(totals).sort((a, b) => b[1] - a[1]).map(([c, m]) => (
              <li key={c}><span>{c}</span><b>{fmtMins(m)}</b></li>
            ))}
          </ul>
        </section>
      )}
      {entries.length === 0
        ? <p className="muted empty">No hours yet — start the timer when you walk in, or add an entry.</p>
        : <ul className="asn-list hours-list">
            {entries.map((e) => <HoursEntryRow key={e.id} e={e} onRefresh={refresh} />)}
          </ul>}
    </div>
  );
}

// G·3 — the semester-zero guide, DYLAN'S surfaces only: hub, capture→Anki export, Hours, Today panel,
// calendar. NO srs and NO tutor sections — his review surface is external Anki, and neither exists in
// this app. Static + read-only; ships inside the bundle, works offline.
function GuideView({ onBack }) {
  return (
    <div className="study guide">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
      </header>
      <div className="study-head"><h1>Guide</h1></div>
      <p className="muted">How each part works. Skim it now, come back whenever something surprises you.</p>

      <section className="guide-section" id="g-hub">
        <h2>Courses &amp; Assignments</h2>
        <p>Every course, lab block, and due date in one place. Add your D1 schedule (or drop a syllabus
        PDF — it reads out the assignments, exams, and grading weights for you), check things off as they
        land, and enter grades so the GPA tile stays honest.</p>
      </section>

      <section className="guide-section" id="g-capture">
        <h2>Study &amp; Cards → Anki</h2>
        <p>Paste lecture notes — or snap a photo or attach a PDF — and cards are generated for you to
        <b> vet before anything is saved</b>. Vetted cards sit in a pending queue until you push them:
        the <b>Anki</b> tile sends them to Anki Desktop on this laptop, and from there they review on
        any device. <b>Your review lives in Anki</b> — this app is the capture-and-vet front end, so a
        card is never "studied" here, only built here.</p>
      </section>

      <section className="guide-section" id="g-notes">
        <h2>Study Notes</h2>
        <p>On a capture — paste, photo, or PDF — you pick what to make: <b>Cards</b>, <b>Study
        notes</b>, or <b>Both</b>. Study notes reorganize a lecture into sections you can skim:
        headings, tight bullets, key terms, and mechanisms kept as mechanisms (the order of steps and
        what causes what stays intact). They contain <b>only what was in the source</b> — nothing is
        added, so a lecture&apos;s gaps stay gaps rather than being filled in for you.</p>
        <p><i>Notes orient you; cards make it stick.</i> Your cards leave for Anki — the notes stay
        here, which makes them the thing you read in-app to get oriented before you drill in Anki.
        Nothing about notes is scheduled or reviewed here.</p>
        <p>Pick <b>Both</b> and each card is tagged with the note section it came from, so you can
        trace a card back to its lecture. That tag is <b>in-app only — it is never pushed to
        Anki</b>; your Anki notes carry exactly the fields they always have. The <b>Study Notes</b>
        tile keeps everything, newest first, until you delete it.</p>
      </section>

      <section className="guide-section" id="g-hours">
        <h2>Hours Log</h2>
        <p>Clinical and shadowing hours keep counting through dental school — <b>residency and
        scholarship applications ask for documented, dated hours</b>, and contemporaneous records beat
        reconstructed ones. Start the timer when you begin (it survives closing the app and can be
        stopped from another device), or add an entry by hand. <b>Export CSV</b> hands you the whole
        log when an application wants it.</p>
      </section>

      <section className="guide-section" id="g-today">
        <h2>The Today panel</h2>
        <p>The dashboard's Today panel is the anti-all-nighter: what's overdue or due today ("do this
        today") plus the one nearest upcoming thing worth starting early.</p>
      </section>

      <section className="guide-section" id="g-schedule">
        <h2>Class schedule</h2>
        <p>Under Courses, <b>Class schedule</b> takes your D1 timetable — paste it or drop the PDF — and
        lays out your lectures and lab blocks as a <b>weekly grid</b>. Those class times also ride your
        calendar feed, so your phone shows them alongside due dates. Saving a schedule replaces the one
        for that term, so re-importing is safe.</p>
      </section>

      <section className="guide-section" id="g-calendar">
        <h2>Calendar</h2>
        <p>Open <b>Calendar</b> from the dashboard for a month grid. Your courses, assignments, exams,
        and class blocks show up automatically, each in its <b>course color</b>. Tap any day to see
        everything on it, and tap <b>+ Add</b> to add your own plans — clinic, shadowing, an
        appointment. Choose <b>Just once</b> or <b>Repeats weekly</b> (pick the weekdays and a date
        range), pick a color, and it lands on the grid. Only your own events can be edited or deleted
        here; assignments and exams are managed under Courses. (This is separate from your Anki review —
        the calendar never touches your cards.)</p>
        <p>Under Courses, <b>Calendar export</b> downloads your whole schedule — assignments, exams,
        classes, and your own events — as a file any calendar app can open. The <b>feed link</b> is the
        live version — your phone's calendar subscribes to it and stays current. The link is shown once
        when you create it; <b>regenerating makes a new link and the old one stops working</b>, so
        re-subscribe after.</p>
      </section>
    </div>
  );
}



// S2·2 — Settings: change password (requires the current one; the server enforces bounds + audits).
// NOTE: existing signed-in devices stay signed in after a change (stateless sessions) — the
// invalidate-on-change follow-up is flagged in PHASE-5, deliberately not built yet.
function SettingsView({ onBack }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState(null); // { kind: "ok" | "err", text }
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!current || !next) { setMsg({ kind: "err", text: "Fill in both fields." }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await api.changePassword(current, next);
      if (r.ok) { setMsg({ kind: "ok", text: "Password changed. Use the new one next time you sign in." }); setCurrent(""); setNext(""); }
      else setMsg({ kind: "err", text: r.data?.error ?? "That didn't work — check your details and try again." });
    } catch {
      setMsg({ kind: "err", text: "Couldn't reach the server — try again." });
    } finally { setBusy(false); }
  }

  return (
    <div className="study">
      <header className="study-top">
        <button className="link" onClick={onBack}>← Dashboard</button>
      </header>
      <div className="study-head"><h1>Settings</h1></div>
      <form className="card settings-card" onSubmit={submit}>
        <h2 className="settings-h">Change password</h2>
        <label className="field"><span>Current password</span>
          <input type="password" value={current} autoComplete="current-password" onChange={(e) => setCurrent(e.target.value)} /></label>
        <label className="field"><span>New password (at least 8 characters)</span>
          <input type="password" value={next} autoComplete="new-password" onChange={(e) => setNext(e.target.value)} /></label>
        <button className="btn" type="submit" disabled={busy}>{busy ? "Changing…" : "Change password"}</button>
        {msg && <p className={msg.kind === "ok" ? "settings-ok" : "error"} role={msg.kind === "ok" ? "status" : "alert"}>{msg.text}</p>}
      </form>
      <DownloadMyData />
    </div>
  );
}

// DX·1 — "Download my data": his whole capture queue + hours + courses as one JSON file, plus a
// human-readable card list as markdown. His data, his copy, one tap.
function DownloadMyData() {
  return (
    <div className="card settings-card">
      <h2 className="settings-h">Download my data</h2>
      <p className="muted">Your cards, hours, and courses — your copy to keep.</p>
      <div className="settings-downloads">
        <a className="btn" href="/api/export.json">Download everything (JSON)</a>
        <a className="link" href="/api/export-cards.md">Just my cards (Markdown)</a>
      </div>
    </div>
  );
}

function Shell({ user, onSignOut }) {
  const [view, setView] = useState("dashboard"); // "dashboard" | "courses" | "cards" | "anki"
  return (
    <div className="app">
      <header className="topbar">
        <button className="wordmark-btn" onClick={() => setView("dashboard")} aria-label="Home"><Wordmark /></button>
        <div className="topbar-right">
          <span className="who">{user.email}</span>
          <button className="link" onClick={() => setView("settings")}>Settings</button>
          <button className="link" onClick={onSignOut}>Sign out</button>
        </div>
      </header>
      <main className="content">
        {view === "courses"
          ? <CoursesView onBack={() => setView("dashboard")} />
          : view === "calendar"
          ? <CalendarView onBack={() => setView("dashboard")} />
          : view === "cards"
            ? <CardsView onBack={() => setView("dashboard")} />
            : view === "anki"
              ? <AnkiView onBack={() => setView("dashboard")} />
              : view === "hours"
                ? <HoursView onBack={() => setView("dashboard")} />
                : view === "settings"
                  ? <SettingsView onBack={() => setView("dashboard")} />
                : view === "guide"
                  ? <GuideView onBack={() => setView("dashboard")} />
                : view === "notes"
                  ? <NotesView onBack={() => setView("dashboard")} />
                : view === "library"
                  ? <LibraryView onBack={() => setView("dashboard")} />
                  : <Dashboard onOpenCourses={() => setView("courses")} onOpenCalendar={() => setView("calendar")}
                      onOpenCards={() => setView("cards")}
                      onOpenAnki={() => setView("anki")} onOpenHours={() => setView("hours")}
                      onOpenGuide={() => setView("guide")} onOpenNotes={() => setView("notes")} onOpenLibrary={() => setView("library")} />}
      </main>
      {/* V·1: the build baked into THIS bundle (esbuild define from sw.js VERSION) — a stale install shows the old string. */}
      <footer className="app-footer">{__APP_VERSION__}</footer>
    </div>
  );
}

function App() {
  const [status, setStatus] = useState("checking"); // "checking" | "out" | "in"
  const [user, setUser] = useState(null);

  useEffect(() => {
    let alive = true;
    api.me().then((u) => {
      if (!alive) return;
      if (u) { setUser(u); setStatus("in"); } else setStatus("out");
    });
    return () => { alive = false; };
  }, []);

  async function signOut() {
    await api.signOut();
    setUser(null);
    setStatus("out");
  }

  if (status === "checking") return <main className="splash"><Wordmark /></main>;
  if (status === "in") return <Shell user={user} onSignOut={signOut} />;
  return <AuthScreen onSignedIn={(u) => { setUser(u); setStatus("in"); }} />;
}

const root = document.getElementById("root");
if (root) createRoot(root).render(<App />);

// Register the service worker (offline shell + installability). Guarded so it no-ops in
// environments without it (e.g. the jsdom smoke test).
if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
